<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import MessageApi from 'dashboard/api/inbox/message.js';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
  currentChat: {
    type: Object,
    default: () => ({}),
  },
  channelType: {
    type: String,
    default: '',
  },
  activeBookingCount: {
    type: Number,
    default: 0,
  },
  activeBookingIds: {
    type: String,
    default: '',
  },
  activeBookingSummaryLines: {
    type: Array,
    default: () => [],
  },
});

const { t, locale } = useI18n();
const { accountScopedRoute } = useAccount();
const store = useStore();
const route = useRoute();

const sourceModule = 'chatwoot_action_center';
const conversationAlertsEndpoint =
  '/api/chatwoot/automation/conversation-alerts';
const tomorrowPickupDraftEndpoint =
  '/api/chatwoot/automation/tomorrow-pickup-draft';
const tourSuggestionsEndpoint = '/api/chatwoot/automation/tour-suggestions';
const sendingKey = ref('');
const activeGlobalAction = ref('');
const activeBookingAction = ref('');
const conversationAlertsResponse = ref(null);
const tourSuggestionResults = ref([]);
const tourSuggestionInputEcho = ref(null);
const localVietvedLifecycleStage = ref('');
const vietvedLifecycleNote = ref('');

const automationRequestState = reactive({
  isRefreshingAlerts: false,
  alertsError: '',
  alertsUpdatedAt: '',
  isLoadingPickupDraft: false,
  isLoadingSuggestions: false,
  suggestionsError: '',
  suggestionsUpdatedAt: '',
});

const singleBookingForm = reactive({
  service: '',
  date: '',
  bookingType: 'ticket',
  adults: 1,
  children: 0,
  infants: 0,
  hotelName: '',
  pickupTime: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  note: '',
});

const manyBookingCommonForm = reactive({
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  managerNote: '',
});

const tourSuggestionForm = reactive({
  dateFrom: '',
  dateTo: '',
  adults: 2,
  children: 0,
  infants: 0,
  hotelName: '',
  interest: '',
});

const vietvedLifecycleLabels = {
  new_lead: 'vietved-life-new-lead',
  request: 'vietved-life-request',
  waiting_code: 'vietved-life-waiting-code',
  client: 'vietved-life-client',
};
const vietvedLifecycleLabelPrefix = 'vietved-life-';
const vietvedLifecycleLabelSet = new Set(Object.values(vietvedLifecycleLabels));

const createManyBookingRow = () => ({
  id: `many-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  service: '',
  date: '',
  bookingType: 'ticket',
  adults: 1,
  children: 0,
  infants: 0,
  hotelName: '',
  pickupTime: '',
  note: '',
});

const manyBookingRows = ref([createManyBookingRow()]);

const updateForms = reactive({});
const assignGuideForms = reactive({});
const setStatusForms = reactive({});
const generateQrForms = reactive({});
const qrStatusForms = reactive({});
const cancelForms = reactive({});

const defaultContactName = computed(() =>
  normalizeString(props.contact?.name) || null
);
const defaultContactPhone = computed(() =>
  normalizeString(props.contact?.phone_number) || null
);
const defaultContactEmail = computed(() =>
  normalizeString(props.contact?.email) || null
);
const defaultContactId = computed(() =>
  props.contact?.id ? `chatwoot_contact:${props.contact.id}` : null
);
const defaultConversationId = computed(() =>
  props.conversationId ? `chatwoot:${props.conversationId}` : null
);
const defaultChannel = computed(
  () =>
    normalizeString(props.channelType) ||
    normalizeString(props.currentChat?.meta?.channel) ||
    null
);

const contactCustomAttributes = computed(
  () => props.contact?.custom_attributes || {}
);
const contactAdditionalAttributes = computed(
  () => props.contact?.additional_attributes || {}
);
const conversationCustomAttributes = computed(
  () =>
    props.currentChat?.custom_attributes ||
    props.currentChat?.customAttributes ||
    props.currentChat?.additional_attributes ||
    {}
);

const vietvedLifecycleStages = computed(() =>
  ['new_lead', 'request', 'waiting_code', 'client'].map(stageKey => ({
    key: stageKey,
    conversationLabel: vietvedLifecycleLabels[stageKey],
    label: t(`CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.STAGES.${stageKey}.LABEL`),
    description: t(
      `CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.STAGES.${stageKey}.DESCRIPTION`
    ),
    actionTitle: t(
      `CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.STAGES.${stageKey}.ACTION`
    ),
    draftText: t(
      `CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.STAGES.${stageKey}.DRAFT`
    ),
  }))
);

function vietvedLifecycleFilterRoute(stage) {
  return accountScopedRoute('label_conversations', {
    label: stage?.conversationLabel || '',
  });
}

const vietvedLifecycleStageKey = computed(() => {
  const raw =
    localVietvedLifecycleStage.value ||
    conversationCustomAttributes.value.vietved_lifecycle_stage ||
    contactCustomAttributes.value.vietved_lifecycle_stage ||
    contactAdditionalAttributes.value.vietved_lifecycle_stage ||
    '';

  return normalizeLifecycleStage(raw);
});

const currentVietvedLifecycleStage = computed(
  () =>
    vietvedLifecycleStages.value.find(
      stage => stage.key === vietvedLifecycleStageKey.value
    ) || null
);

const parsedBookingsSource = computed(() => {
  const candidates = [
    contactCustomAttributes.value.active_bookings_json,
    contactCustomAttributes.value.active_bookings,
    contactAdditionalAttributes.value.active_bookings_json,
    contactAdditionalAttributes.value.active_bookings,
  ];

  for (const candidate of candidates) {
    const parsed = parsePossibleJson(candidate);
    if (Array.isArray(parsed)) {
      return parsed;
    }

    if (parsed && typeof parsed === 'object') {
      if (Array.isArray(parsed.bookings)) {
        return parsed.bookings;
      }

      if (Array.isArray(parsed.data)) {
        return parsed.data;
      }

      return Object.values(parsed);
    }
  }

  return [];
});

const customConversationAlertsSource = computed(() => {
  const candidates = [
    contactCustomAttributes.value.conversation_alerts_json,
    contactCustomAttributes.value.conversation_alerts,
    contactCustomAttributes.value.booking_alerts_json,
    contactCustomAttributes.value.booking_alerts,
    contactAdditionalAttributes.value.conversation_alerts_json,
    contactAdditionalAttributes.value.conversation_alerts,
    contactAdditionalAttributes.value.booking_alerts_json,
    contactAdditionalAttributes.value.booking_alerts,
  ];

  return firstParsedCollection(candidates, ['alerts', 'data']);
});

const customTourSuggestionsSource = computed(() => {
  const candidates = [
    contactCustomAttributes.value.tour_suggestions_json,
    contactCustomAttributes.value.tour_suggestions,
    contactCustomAttributes.value.recommended_tours_json,
    contactCustomAttributes.value.recommended_tours,
    contactAdditionalAttributes.value.tour_suggestions_json,
    contactAdditionalAttributes.value.tour_suggestions,
    contactAdditionalAttributes.value.recommended_tours_json,
    contactAdditionalAttributes.value.recommended_tours,
  ];

  return firstParsedCollection(candidates, ['suggestions', 'tours', 'data']);
});

const normalizedBookings = computed(() => {
  const cards = parsedBookingsSource.value
    .map((booking, index) => normalizeBooking(booking, index))
    .filter(Boolean);

  if (!cards.length) {
    return props.activeBookingSummaryLines
      .map((line, index) => normalizeSummaryLine(line, index))
      .filter(Boolean)
      .sort(compareBookings);
  }

  const summaryMap = new Map(
    props.activeBookingSummaryLines
      .map((line, index) => normalizeSummaryLine(line, index))
      .filter(Boolean)
      .map(summary => [summary.bookingId || summary.key, summary])
  );

  return cards
    .map(booking => {
      const summary = summaryMap.get(booking.bookingId || booking.key);
      return summary
        ? {
            ...summary,
            ...booking,
            summaryLine: booking.summaryLine || summary.summaryLine,
          }
        : booking;
    })
    .sort(compareBookings);
});

const bookingCountLabel = computed(() => {
  const fallbackCount = Number(props.activeBookingCount || 0);
  const count = normalizedBookings.value.length || fallbackCount;

  return count > 0 ? String(count) : '0';
});

const bookingIdsLabel = computed(() => {
  if (normalizeString(props.activeBookingIds)) {
    return props.activeBookingIds;
  }

  return normalizedBookings.value
    .map(booking => (booking.bookingId ? `#${booking.bookingId}` : null))
    .filter(Boolean)
    .join(', ');
});

const allBookingIdsLabel = computed(() => {
  const candidates = [
    contactCustomAttributes.value.all_booking_ids,
    contactAdditionalAttributes.value.all_booking_ids,
    bookingIdsLabel.value,
  ];

  return candidates
    .map(candidate => normalizeBookingIdsLabel(candidate))
    .find(Boolean) || '';
});

const hasBookings = computed(() => normalizedBookings.value.length > 0);

const selectedChatMessages = computed(() =>
  Array.isArray(props.currentChat?.messages) ? props.currentChat.messages : []
);

const cachedConversationAlerts = computed(() =>
  normalizeAlertList(customConversationAlertsSource.value, 'cached-alert')
);

const localConversationAlerts = computed(() =>
  buildLocalConversationAlerts(normalizedBookings.value)
);

const remoteConversationAlerts = computed(() =>
  normalizeAlertList(conversationAlertsResponse.value?.alerts, 'remote-alert')
);

const conversationAlerts = computed(() => {
  if (remoteConversationAlerts.value.length) {
    return remoteConversationAlerts.value;
  }

  if (cachedConversationAlerts.value.length) {
    return cachedConversationAlerts.value;
  }

  return localConversationAlerts.value;
});

const conversationAlertsSummary = computed(() => {
  const remoteSummary = formatAutomationSummary(
    conversationAlertsResponse.value?.summary
  );
  if (remoteSummary) {
    return remoteSummary;
  }

  if (conversationAlerts.value.length) {
    return t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.SUMMARY_WITH_COUNT', {
      count: conversationAlerts.value.length,
    });
  }

  return t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.EMPTY');
});

const conversationAlertsSourceLabel = computed(() => {
  if (remoteConversationAlerts.value.length) {
    return t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.SOURCE_PROXY');
  }

  if (cachedConversationAlerts.value.length) {
    return t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.SOURCE_ATTRIBUTES');
  }

  return t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.SOURCE_LOCAL');
});

const criticalAlertCount = computed(
  () =>
    conversationAlerts.value.filter(alert =>
      ['danger', 'warning'].includes(alert.tone)
    ).length
);

const cachedTourSuggestions = computed(() =>
  normalizeSuggestionList(customTourSuggestionsSource.value, 'cached-suggestion')
);

const displayedTourSuggestions = computed(() => {
  if (tourSuggestionResults.value.length) {
    return tourSuggestionResults.value;
  }

  return cachedTourSuggestions.value;
});

const tourSuggestionsSourceLabel = computed(() => {
  if (tourSuggestionResults.value.length) {
    return t('CONTACT_PANEL.ACTION_CENTER.TOUR_SUGGESTIONS.SOURCE_PROXY');
  }

  if (cachedTourSuggestions.value.length) {
    return t('CONTACT_PANEL.ACTION_CENTER.TOUR_SUGGESTIONS.SOURCE_ATTRIBUTES');
  }

  return t('CONTACT_PANEL.ACTION_CENTER.TOUR_SUGGESTIONS.SOURCE_EMPTY');
});

const tomorrowDateKey = computed(() => dateKeyWithOffset(1));

const tomorrowPickupBooking = computed(() => {
  const targetDate = tomorrowDateKey.value;
  const candidates = normalizedBookings.value
    .filter(booking => {
      const status = normalizeBookingStatusValue(booking.status);
      const pickupTime = normalizeString(
        booking.pickupPublishedTime ||
          (status === 'time_sent' ? booking.pickupTime : '')
      );

      return (
        targetDate &&
        dateKeyFromValue(booking.startDate) === targetDate &&
        pickupTime &&
        status !== 'cancelled'
      );
    })
    .sort((left, right) => {
      const leftStatusPriority =
        normalizeBookingStatusValue(left.status) === 'time_sent' ? 0 : 1;
      const rightStatusPriority =
        normalizeBookingStatusValue(right.status) === 'time_sent' ? 0 : 1;

      if (leftStatusPriority !== rightStatusPriority) {
        return leftStatusPriority - rightStatusPriority;
      }

      return normalizeString(left.pickupTime).localeCompare(
        normalizeString(right.pickupTime)
      );
    });

  return candidates[0] || null;
});

const tomorrowPickupSummary = computed(() => {
  const booking = tomorrowPickupBooking.value;
  if (!booking) {
    return t('CONTACT_PANEL.ACTION_CENTER.TOMORROW_PICKUP.DISCOVER');
  }

  const bits = [
    booking.bookingId ? `#${booking.bookingId}` : null,
    normalizeString(booking.tour),
    normalizeString(booking.pickupPublishedTime || booking.pickupTime)
      ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.PICKUP_TIME')}: ${
          booking.pickupPublishedTime || booking.pickupTime
        }`
      : null,
  ].filter(Boolean);

  return bits.join(' · ');
});

function parsePossibleJson(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === 'object') {
    return value;
  }

  const normalized = normalizeString(value);
  if (!normalized) {
    return null;
  }

  try {
    return JSON.parse(normalized);
  } catch (error) {
    return null;
  }
}

function firstParsedCollection(candidates, objectKeys = []) {
  for (const candidate of candidates) {
    const parsed = parsePossibleJson(candidate);
    if (Array.isArray(parsed)) {
      return parsed;
    }

    if (parsed && typeof parsed === 'object') {
      for (const key of objectKeys) {
        if (Array.isArray(parsed[key])) {
          return parsed[key];
        }
      }

      const values = Object.values(parsed).filter(Boolean);
      if (values.length && values.every(value => typeof value === 'object')) {
        return values;
      }
    }
  }

  return [];
}

function normalizeString(value) {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value).trim();
}

function padDatePart(value) {
  return String(value).padStart(2, '0');
}

function dateKeyWithOffset(dayOffset = 0) {
  const date = new Date();
  date.setDate(date.getDate() + dayOffset);

  return [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join('-');
}

function dateKeyFromValue(value) {
  const normalized = normalizeString(value);
  if (!normalized) {
    return '';
  }

  const isoMatch = normalized.match(/\b(\d{4})-(\d{2})-(\d{2})\b/u);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const ruMatch = normalized.match(/\b(\d{2})\.(\d{2})\.(\d{4})\b/u);
  if (ruMatch) {
    return `${ruMatch[3]}-${ruMatch[2]}-${ruMatch[1]}`;
  }

  return '';
}

function normalizeLifecycleStage(value) {
  const normalized = normalizeString(value)
    .toLowerCase()
    .replaceAll('-', '_')
    .replaceAll(' ', '_')
    .replace(/_+/g, '_');

  const aliases = {
    new: 'new_lead',
    lead: 'new_lead',
    newlead: 'new_lead',
    new_lead: 'new_lead',
    request: 'request',
    deal: 'request',
    waiting_code: 'waiting_code',
    wait_code: 'waiting_code',
    code: 'waiting_code',
    client: 'client',
    customer: 'client',
  };

  return aliases[normalized] || '';
}

// DV: account 1 (Дядя Ваня Вьетнам) does not use the Vietved partner funnel.
const isDvAccount = computed(
  () => normalizeNumber(route.params.accountId) === 1
);

const clientPortalUrl = computed(() => {
  const fromBooking = normalizedBookings.value.find(
    booking => booking?.clientPortalUrl
  )?.clientPortalUrl;

  return (
    fromBooking ||
    normalizeString(conversationCustomAttributes.value.client_portal_url) ||
    ''
  );
});

const dvBookingCards = computed(() =>
  normalizedBookings.value.map(booking => {
    const due = normalizeMoney(booking.amountDue);
    const total = normalizeMoney(booking.totalPrice);
    const paid = normalizeMoney(booking.paidAmount);
    const cancelled = isCancelledBooking(booking);
    const alerts = cancelled
      ? []
      : localConversationAlerts.value.filter(
          alert =>
            alert.bookingId &&
            alert.bookingId === booking.bookingId &&
            alert.category !== 'payment' &&
            alert.tone === 'danger'
        );

    return {
      ...booking,
      cancelled,
      dateLabel: dvDateLabel(booking.startDate),
      statusLabel: bookingStatusLabel(booking.status),
      statusTone: dvStatusTone(booking.status),
      pickupLabel: dvPickupLabel(booking),
      guestsLabel: dvGuestsLabel(booking.guests),
      paymentLabel: dvPaymentLabel({ due, total, paid }),
      paymentTone: due !== null && due > 0 ? 'warning' : 'success',
      alerts,
    };
  })
);

const dvPickupTileHint = computed(() => {
  const booking = tomorrowPickupBooking.value;
  if (!booking) {
    return t('CONTACT_PANEL.ACTION_CENTER.DV_TILES.PICKUP_NONE');
  }
  const time = normalizeString(
    booking.pickupPublishedTime || booking.pickupTime
  );
  return [time, normalizeString(booking.tour)].filter(Boolean).join(' · ') ||
    (booking.bookingId ? `#${booking.bookingId}` : '');
});

const hasSeveralPortalLinks = computed(
  () =>
    new Set(
      normalizedBookings.value
        .map(booking => booking.clientPortalUrl)
        .filter(Boolean)
    ).size > 1
);

function dvDateLabel(dateValue) {
  const date = parseDateOnly(dateValue);
  if (!date) {
    return t('CONTACT_PANEL.ACTION_CENTER.UNKNOWN_SHORT');
  }

  const formatted = new Intl.DateTimeFormat(locale.value || 'ru', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).format(date);
  const daysUntil = daysUntilDate(dateValue);
  if (daysUntil === 0) {
    return `${formatted} · ${t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.TODAY')}`;
  }
  if (daysUntil === 1) {
    return `${formatted} · ${t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.TOMORROW')}`;
  }
  return formatted;
}

// Chatwoot's palette has n-teal / n-amber / n-ruby / n-blue scales,
// plain Tailwind emerald / amber / rose classes are not generated.
const DV_TONE_TEXT = {
  success: 'text-n-teal-11',
  warning: 'text-n-amber-11',
  danger: 'text-n-ruby-11',
  brand: 'text-n-blue-11',
  neutral: 'text-n-slate-11',
};

function dvChipClasses(tone = 'neutral') {
  const background = {
    success: 'bg-n-teal-3',
    warning: 'bg-n-amber-3',
    danger: 'bg-n-ruby-3',
    brand: 'bg-n-blue-3',
    neutral: 'bg-n-alpha-2',
  };
  return [
    'inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium leading-4',
    background[tone] || background.neutral,
    DV_TONE_TEXT[tone] || DV_TONE_TEXT.neutral,
  ];
}

function dvStatusTone(status) {
  const normalized = normalizeBookingStatusValue(status);
  if (['confirmed', 'time_sent', 'paid'].includes(normalized)) return 'success';
  if (normalized === 'deposit') return 'brand';
  if (normalized === 'pending') return 'warning';
  if (normalized === 'cancelled') return 'danger';
  return 'neutral';
}

function dvPickupLabel(booking) {
  if (booking.pickupPublishedTime) {
    return booking.pickupPublishedTime;
  }
  if (booking.pickupDraftTime) {
    return t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.PICKUP_DRAFT', {
      time: booking.pickupDraftTime,
    });
  }
  return t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.PICKUP_NONE');
}

function dvGuestsLabel(guests) {
  if (!guests) {
    return '';
  }

  return [
    guests.adult
      ? t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.ADULTS', { count: guests.adult })
      : null,
    guests.child
      ? t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.CHILDREN', { count: guests.child })
      : null,
    guests.infant
      ? t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.INFANTS', { count: guests.infant })
      : null,
  ]
    .filter(Boolean)
    .join(' + ');
}

function dvMoney(amount) {
  const rounded = Math.round(Number(amount) * 100) / 100;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(2)} $`;
}

function dvPaymentLabel({ due, total, paid }) {
  if (total === null) {
    return t('CONTACT_PANEL.ACTION_CENTER.UNKNOWN_SHORT');
  }
  if (due !== null && due > 0 && !paid) {
    return t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.PAYMENT_UNPAID', {
      due: dvMoney(due),
    });
  }
  if (due !== null && due > 0) {
    return t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.PAYMENT_DUE', {
      paid: dvMoney(paid || 0),
      total: dvMoney(total),
      due: dvMoney(due),
    });
  }
  return t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.PAYMENT_PAID', {
    total: dvMoney(total),
  });
}

function openClientPortal() {
  if (!clientPortalUrl.value) return;
  window.open(clientPortalUrl.value, '_blank', 'noopener,noreferrer');
}

async function copyClientPortalUrl() {
  if (!clientPortalUrl.value) return;
  try {
    await copyTextToClipboard(clientPortalUrl.value);
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.CLIENT_PORTAL.COPIED'));
  } catch (error) {
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.CLIENT_PORTAL.COPY_FAILED'));
  }
}

function openBookingPortal(booking) {
  if (!booking?.clientPortalUrl) return;
  window.open(booking.clientPortalUrl, '_blank', 'noopener,noreferrer');
}

function insertClientPortalUrl() {
  if (!clientPortalUrl.value) return;
  insertDraftIntoReplyEditor(clientPortalUrl.value);
}

function normalizeNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeMoney(value) {
  const normalized = normalizeNumber(value);
  return normalized === null ? null : Math.round(normalized * 100) / 100;
}

function normalizeBoolean(value) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  const normalized = normalizeString(value).toLowerCase();
  if (!normalized) {
    return null;
  }

  if (['1', 'true', 'yes', 'y', 'ok', 'да'].includes(normalized)) {
    return true;
  }

  if (['0', 'false', 'no', 'n', 'нет'].includes(normalized)) {
    return false;
  }

  return null;
}

function normalizeGuests(rawBooking) {
  const guests = rawBooking?.guests || {};
  const adults =
    normalizeNumber(
      guests.adult ??
        rawBooking?.adult_count ??
        rawBooking?.adults ??
        rawBooking?.adult
    ) || 0;
  const children =
    normalizeNumber(
      guests.child ??
        rawBooking?.child_count ??
        rawBooking?.children ??
        rawBooking?.child
    ) || 0;
  const infants =
    normalizeNumber(
      guests.infant ??
        rawBooking?.infant_count ??
        rawBooking?.infants ??
        rawBooking?.infant
    ) || 0;

  if (adults === 0 && children === 0 && infants === 0) {
    return null;
  }

  return {
    adult: adults,
    child: children,
    infant: infants,
  };
}

function normalizeBooking(rawBooking, index) {
  if (!rawBooking || typeof rawBooking !== 'object') {
    return null;
  }

  const pickup = rawBooking.pickup || {};
  const docs = rawBooking.docs || {};
  const confirmedPickupTime =
    normalizeString(
      pickup.pickup_time ?? rawBooking.pickup_time ?? rawBooking.pickupTime
    ) || null;
  const draftPickupTime =
    normalizeString(pickup.pickup_time_draft ?? rawBooking.pickup_time_draft) ||
    null;
  const rawQr = rawBooking.qr || rawBooking.qr_data || rawBooking.qrData || {};
  const parsedQr = parsePossibleJson(rawQr);
  const qr =
    rawQr && typeof rawQr === 'object'
      ? rawQr
      : parsedQr && typeof parsedQr === 'object'
        ? parsedQr
        : null;
  const bookingId =
    normalizeNumber(
      rawBooking.booking_id ??
        rawBooking.bookingId ??
        rawBooking.public_id ??
        rawBooking.id
    ) || null;
  const totalPrice = normalizeMoney(
    rawBooking.total_price ?? rawBooking.totalPrice
  );
  const paidAmount = normalizeMoney(
    rawBooking.paid_amount ?? rawBooking.paidAmount
  );
  const derivedAmountDue =
    totalPrice !== null && paidAmount !== null
      ? Math.max(0, Math.round((totalPrice - paidAmount) * 100) / 100)
      : null;

  return {
    key: bookingId ? `booking-${bookingId}` : `booking-fallback-${index}`,
    bookingId,
    tour:
      normalizeString(
        rawBooking.tour ??
          rawBooking.service_title ??
          rawBooking.title ??
          rawBooking.service?.title
      ) || null,
    serviceId:
      normalizeNumber(rawBooking.service_id ?? rawBooking.serviceId) || null,
    startDate:
      normalizeString(
        rawBooking.start_date ?? rawBooking.startDate ?? rawBooking.date
      ) || null,
    status:
      normalizeString(rawBooking.status ?? rawBooking.booking_status) || null,
    paymentStatus:
      normalizeString(
        rawBooking.payment_status ?? rawBooking.paymentStatus
      ) || null,
    hotelName:
      normalizeString(
        rawBooking.hotel_name ?? rawBooking.hotelName ?? pickup.hotel_name
      ) || null,
    guideName:
      normalizeString(
        rawBooking.guide_name ??
          rawBooking.guideName ??
          rawBooking.guide?.name ??
          pickup.guide?.name
      ) || null,
    guideId:
      normalizeNumber(
        rawBooking.guide_id ??
          rawBooking.guideId ??
          rawBooking.guide?.id ??
          pickup.guide?.id
      ) || null,
    totalPrice,
    paidAmount,
    amountDue:
      normalizeMoney(rawBooking.amount_due ?? rawBooking.amountDue) ??
      derivedAmountDue,
    clientPortalUrl:
      normalizeString(
        rawBooking.client_portal_url ??
          rawBooking.clientPortalUrl ??
          rawBooking.portal_url
      ) || null,
    guests: normalizeGuests(rawBooking),
    summaryLine: normalizeString(rawBooking.summary_line) || null,
    pickupTime: confirmedPickupTime || draftPickupTime,
    pickupPublishedTime: confirmedPickupTime,
    pickupDraftTime: draftPickupTime,
    pickupAssigned:
      normalizeBoolean(pickup.pickup_assigned ?? rawBooking.pickup_assigned) ??
      !!confirmedPickupTime,
    pickupTransferRequired:
      normalizeBoolean(
        pickup.pickup_transfer_required ?? rawBooking.pickup_transfer_required
      ) || false,
    pickupTransferTime:
      normalizeString(
        pickup.pickup_transfer_time ?? rawBooking.pickup_transfer_time
      ) || null,
    pickupTransferNote:
      normalizeString(
        pickup.pickup_transfer_note ?? rawBooking.pickup_transfer_note
      ) || null,
    guidePickupDetails:
      normalizeString(
        pickup.guide_pickup_details ?? rawBooking.guide_pickup_details
      ) || null,
    docsReady:
      normalizeBoolean(docs.docs_ready ?? rawBooking.docs_ready) ?? null,
    missingItems: Array.isArray(docs.missing_items ?? rawBooking.missing_items)
      ? (docs.missing_items ?? rawBooking.missing_items).filter(Boolean)
      : [],
    passportRequired:
      normalizeBoolean(
        docs.passport_required ?? rawBooking.passport_required
      ) || false,
    passportNeeded:
      normalizeNumber(docs.passport_needed ?? rawBooking.passport_needed) || 0,
    passportUploaded:
      normalizeNumber(
        docs.passport_uploaded ?? rawBooking.passport_uploaded
      ) || 0,
    passportNamesRequired:
      normalizeBoolean(
        docs.passport_names_required ?? rawBooking.passport_names_required
      ) || false,
    hotelStatus:
      normalizeString(
        docs.hotel_status ??
          rawBooking.hotel_status ??
          rawBooking.hotelStatus
      ) || null,
    hotelVoucherUrl:
      normalizeString(
        docs.hotel_voucher_url ?? rawBooking.hotel_voucher_url
      ) || null,
    hotelVoucherUrls: Array.isArray(
      docs.hotel_voucher_urls ?? rawBooking.hotel_voucher_urls
    )
      ? docs.hotel_voucher_urls ?? rawBooking.hotel_voucher_urls
      : [],
    qrData: qr,
    raw: rawBooking,
  };
}

function normalizeSummaryLine(line, index) {
  const normalizedLine = normalizeString(line);
  if (!normalizedLine) {
    return null;
  }

  const parts = normalizedLine
    .split('|')
    .map(part => part.trim())
    .filter(Boolean);
  const bookingIdMatch = parts[0]?.match(/#?(\d{1,12})/u);

  return {
    key: `summary-${index}`,
    bookingId: bookingIdMatch ? Number(bookingIdMatch[1]) : null,
    tour: parts[1] || null,
    startDate: parts[2] || null,
    status: parts[3] || null,
    paymentStatus: null,
    hotelName: null,
    guideName: null,
    guideId: null,
    totalPrice: null,
    paidAmount: null,
    amountDue: null,
    clientPortalUrl: null,
    guests: null,
    summaryLine: normalizedLine,
    pickupTime: null,
    pickupPublishedTime: null,
    pickupDraftTime: null,
    pickupAssigned: null,
    pickupTransferRequired: false,
    pickupTransferTime: null,
    pickupTransferNote: null,
    guidePickupDetails: null,
    docsReady: null,
    missingItems: [],
    passportRequired: false,
    passportNeeded: 0,
    passportUploaded: 0,
    passportNamesRequired: false,
    hotelStatus: null,
    hotelVoucherUrl: null,
    hotelVoucherUrls: [],
    qrData: null,
    raw: {
      summary_line: normalizedLine,
    },
  };
}

function normalizeBookingIdsLabel(value) {
  const normalized = normalizeString(value);
  if (!normalized) {
    return '';
  }

  const ids = [...normalized.matchAll(/#?\b(\d{3,12})\b/gu)]
    .map(match => Number(match[1]))
    .filter(id => Number.isFinite(id) && id > 0);

  return [...new Set(ids)].map(id => `#${id}`).join(', ');
}

function compareBookings(left, right) {
  const leftDate = normalizeString(left.startDate);
  const rightDate = normalizeString(right.startDate);

  if (leftDate && rightDate) {
    return leftDate.localeCompare(rightDate);
  }

  if (leftDate || rightDate) {
    return leftDate ? -1 : 1;
  }

  return String(left.bookingId || left.key).localeCompare(
    String(right.bookingId || right.key)
  );
}

function openGlobalForm(action) {
  activeGlobalAction.value = activeGlobalAction.value === action ? '' : action;
}

function bookingActionKey(booking, action) {
  return `${booking.key}:${action}`;
}

function openBookingForm(booking, action) {
  const key = bookingActionKey(booking, action);
  activeBookingAction.value = activeBookingAction.value === key ? '' : key;

  if (action === 'update_booking') {
    ensureUpdateForm(booking);
  }

  if (action === 'assign_guide') {
    ensureAssignGuideForm(booking);
  }

  if (action === 'set_booking_status') {
    ensureSetStatusForm(booking);
  }

  if (action === 'generate_payment_qr') {
    ensureGenerateQrForm(booking);
  }

  if (action === 'qr_status') {
    ensureQrStatusForm(booking);
  }

  if (action === 'cancel_booking') {
    ensureCancelForm(booking);
  }
}

function isBookingFormOpen(booking, action) {
  return activeBookingAction.value === bookingActionKey(booking, action);
}

function ensureUpdateForm(booking) {
  if (!updateForms[booking.key]) {
    updateForms[booking.key] = reactive({
      newDate: booking.startDate || '',
      hotelName: booking.hotelName || '',
      adults: booking.guests?.adult ?? '',
      children: booking.guests?.child ?? '',
      infants: booking.guests?.infant ?? '',
    });
  }

  return updateForms[booking.key];
}

function ensureAssignGuideForm(booking) {
  if (!assignGuideForms[booking.key]) {
    assignGuideForms[booking.key] = reactive({
      guide: booking.guideName || '',
      guideId: booking.guideId || '',
      note: '',
    });
  }

  return assignGuideForms[booking.key];
}

function ensureSetStatusForm(booking) {
  if (!setStatusForms[booking.key]) {
    setStatusForms[booking.key] = reactive({
      status: normalizeBookingStatusValue(booking.status) || 'confirmed',
      note: '',
    });
  }

  return setStatusForms[booking.key];
}

function ensureGenerateQrForm(booking) {
  if (!generateQrForms[booking.key]) {
    generateQrForms[booking.key] = reactive({
      paymentType:
        normalizeString(booking.qrData?.payment_type).toLowerCase() === 'full'
          ? 'full'
          : 'deposit',
      depositAmount: '',
      note: '',
    });
  }

  return generateQrForms[booking.key];
}

function ensureQrStatusForm(booking) {
  if (!qrStatusForms[booking.key]) {
    qrStatusForms[booking.key] = reactive({
      refreshProviderStatus: false,
    });
  }

  return qrStatusForms[booking.key];
}

function ensureCancelForm(booking) {
  if (!cancelForms[booking.key]) {
    cancelForms[booking.key] = reactive({
      reason: '',
    });
  }

  return cancelForms[booking.key];
}

function addManyBookingRow() {
  manyBookingRows.value.push(createManyBookingRow());
}

function removeManyBookingRow(rowId) {
  if (manyBookingRows.value.length === 1) {
    manyBookingRows.value = [createManyBookingRow()];
    return;
  }

  manyBookingRows.value = manyBookingRows.value.filter(row => row.id !== rowId);
}

function createIdempotencyKey(action) {
  return [
    'cw-ac',
    action,
    props.conversationId,
    Date.now(),
    Math.random().toString(36).slice(2, 8),
  ].join('-');
}

function compactValue(value) {
  if (Array.isArray(value)) {
    const items = value
      .map(item => compactValue(item))
      .filter(item => item !== null && item !== undefined);

    return items.length ? items : null;
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value).reduce((result, [key, innerValue]) => {
      const compacted = compactValue(innerValue);
      if (
        compacted !== null &&
        compacted !== undefined &&
        !(typeof compacted === 'string' && compacted === '')
      ) {
        result[key] = compacted;
      }
      return result;
    }, {});

    return Object.keys(entries).length ? entries : null;
  }

  if (typeof value === 'string') {
    const normalized = value.trim();
    return normalized === '' ? null : normalized;
  }

  return value === undefined ? null : value;
}

function resolveFilledValue(value, fallback = null) {
  const normalized = normalizeString(value);
  return normalized || fallback;
}

function resolveServiceSelection(rawValue) {
  const value = normalizeString(rawValue);
  if (!value) {
    return null;
  }

  if (/^\d+$/u.test(value)) {
    return {
      service_id: Number(value),
      service_label: `#${value}`,
    };
  }

  if (/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/u.test(value)) {
    return {
      service_slug: value,
      service_label: value,
    };
  }

  return {
    service_query: value,
    service_label: value,
  };
}

function buildBasePayload(action, extra = {}) {
  const accountId = normalizeNumber(route.params.accountId);

  return compactValue({
    action,
    account_id: accountId,
    chatwoot_account_id: accountId,
    requested_by: 'chatwoot_action_center',
    source_module: sourceModule,
    idempotency_key: createIdempotencyKey(action),
    contact_id: defaultContactId.value,
    contact_numeric_id: props.contact?.id || null,
    conversation_id: defaultConversationId.value,
    conversation_numeric_id: normalizeNumber(props.conversationId),
    contact_phone: defaultContactPhone.value,
    contact_email: defaultContactEmail.value,
    contact_name: defaultContactName.value,
    channel: defaultChannel.value,
    ...extra,
  });
}

function buildGuestsPayload({ adults, children, infants }) {
  const adult = normalizeNumber(adults);
  const child = normalizeNumber(children);
  const infant = normalizeNumber(infants);

  if (adult === null && child === null && infant === null) {
    return null;
  }

  return {
    adult: adult ?? 0,
    child: child ?? 0,
    infant: infant ?? 0,
  };
}

function formatMoney(amount) {
  if (amount === null || amount === undefined) {
    return t('CONTACT_PANEL.ACTION_CENTER.UNKNOWN_SHORT');
  }

  return `${Number(amount).toFixed(2)} $`;
}

function formatDate(dateValue) {
  const normalized = normalizeString(dateValue);
  if (!normalized) {
    return t('CONTACT_PANEL.ACTION_CENTER.UNKNOWN_SHORT');
  }

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return normalized;
  }

  return new Intl.DateTimeFormat(locale.value || 'ru', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

function normalizeBookingStatusValue(status) {
  const normalized = normalizeString(status).toLowerCase();

  if (['active', 'approved'].includes(normalized)) {
    return 'pending';
  }

  return normalized;
}

function bookingStatusLabel(status) {
  const normalized = normalizeBookingStatusValue(status);
  if (!normalized) {
    return t('CONTACT_PANEL.ACTION_CENTER.UNKNOWN_SHORT');
  }

  const key = `CONTACT_PANEL.ACTION_CENTER.BOOKING_STATUS.${normalized}`;
  const translation = t(key);
  return translation === key ? normalized : translation;
}

function paymentStatusLabel(status) {
  const normalized = normalizeString(status).toLowerCase();
  if (!normalized) {
    return t('CONTACT_PANEL.ACTION_CENTER.UNKNOWN_SHORT');
  }

  const key = `CONTACT_PANEL.ACTION_CENTER.PAYMENT_STATUS.${normalized}`;
  const translation = t(key);
  return translation === key ? normalized : translation;
}

function resolveQrStatus(booking) {
  const qrData = booking.qrData && typeof booking.qrData === 'object' ? booking.qrData : null;
  const paymentStatus = normalizeString(booking.paymentStatus).toLowerCase();

  if (paymentStatus === 'paid') {
    return {
      key: 'paid',
      label: t('CONTACT_PANEL.ACTION_CENTER.QR_STATUS.paid'),
      tone: 'success',
    };
  }

  if (
    paymentStatus === 'partial' &&
    Number(booking.paidAmount || 0) > 0 &&
    normalizeString(qrData?.payment_type).toLowerCase() === 'deposit'
  ) {
    return {
      key: 'deposit_received',
      label: t('CONTACT_PANEL.ACTION_CENTER.QR_STATUS.deposit_received'),
      tone: 'success',
    };
  }

  if (!qrData || !normalizeString(qrData.order_uuid)) {
    return {
      key: 'not_generated',
      label: t('CONTACT_PANEL.ACTION_CENTER.QR_STATUS.not_generated'),
      tone: 'neutral',
    };
  }

  const expiresAt = normalizeString(qrData.expires_at);
  if (expiresAt) {
    const expiresDate = new Date(expiresAt);
    if (!Number.isNaN(expiresDate.getTime()) && expiresDate.getTime() < Date.now()) {
      return {
        key: 'expired',
        label: t('CONTACT_PANEL.ACTION_CENTER.QR_STATUS.expired'),
        tone: 'warning',
      };
    }
  }

  return {
    key: 'created',
    label: t('CONTACT_PANEL.ACTION_CENTER.QR_STATUS.created'),
    tone: 'brand',
  };
}

function resolvePickupStatus(booking) {
  if (normalizeBookingStatusValue(booking.status) === 'cancelled') {
    return {
      key: 'cancelled',
      label: t('CONTACT_PANEL.ACTION_CENTER.PICKUP_STATUS.cancelled'),
      tone: 'danger',
    };
  }

  if (booking.pickupAssigned === true) {
    return {
      key: 'assigned',
      label: normalizeString(booking.pickupTime)
        ? t('CONTACT_PANEL.ACTION_CENTER.PICKUP_STATUS.assigned_with_time', {
            time: booking.pickupTime,
          })
        : t('CONTACT_PANEL.ACTION_CENTER.PICKUP_STATUS.assigned'),
      tone: 'success',
    };
  }

  if (normalizeString(booking.pickupDraftTime)) {
    return {
      key: 'draft',
      label: t('CONTACT_PANEL.ACTION_CENTER.PICKUP_STATUS.draft', {
        time: booking.pickupDraftTime,
      }),
      tone: 'warning',
    };
  }

  return {
    key: 'pending',
    label: t('CONTACT_PANEL.ACTION_CENTER.PICKUP_STATUS.pending'),
    tone: 'warning',
  };
}

function resolveDocsStatus(booking) {
  if (normalizeBookingStatusValue(booking.status) === 'cancelled') {
    return {
      key: 'cancelled',
      label: t('CONTACT_PANEL.ACTION_CENTER.DOCS_STATUS.cancelled'),
      tone: 'danger',
    };
  }

  if (booking.docsReady === true) {
    return {
      key: 'ready',
      label: t('CONTACT_PANEL.ACTION_CENTER.DOCS_STATUS.ready'),
      tone: 'success',
    };
  }

  if (Array.isArray(booking.missingItems) && booking.missingItems.length) {
    return {
      key: 'missing',
      label: t('CONTACT_PANEL.ACTION_CENTER.DOCS_STATUS.missing', {
        count: booking.missingItems.length,
      }),
      tone: 'warning',
    };
  }

  if (booking.passportRequired || booking.hotelStatus || booking.hotelVoucherUrl) {
    return {
      key: 'in_progress',
      label: t('CONTACT_PANEL.ACTION_CENTER.DOCS_STATUS.in_progress'),
      tone: 'brand',
    };
  }

  return {
    key: 'unknown',
    label: t('CONTACT_PANEL.ACTION_CENTER.DOCS_STATUS.unknown'),
    tone: 'neutral',
  };
}

function normalizeTone(value) {
  const normalized = normalizeString(value).toLowerCase();

  if (['critical', 'danger', 'error', 'overdue', 'red'].includes(normalized)) {
    return 'danger';
  }

  if (['warning', 'warn', 'pending', 'amber', 'yellow'].includes(normalized)) {
    return 'warning';
  }

  if (['success', 'ok', 'ready', 'green'].includes(normalized)) {
    return 'success';
  }

  if (['info', 'brand', 'blue'].includes(normalized)) {
    return 'brand';
  }

  return 'neutral';
}

function normalizeStringArray(value) {
  if (Array.isArray(value)) {
    return value.map(item => normalizeString(item)).filter(Boolean);
  }

  const normalized = normalizeString(value);
  if (!normalized) {
    return [];
  }

  return normalized
    .split(/\n|;|\|/u)
    .map(item => item.trim())
    .filter(Boolean);
}

function formatAutomationSummary(value) {
  const parsed = parseMaybeJson(value);
  if (!parsed) {
    return '';
  }

  if (typeof parsed === 'string' || typeof parsed === 'number') {
    return normalizeString(parsed);
  }

  if (typeof parsed === 'object') {
    return Object.entries(parsed)
      .filter(([, innerValue]) =>
        ['string', 'number', 'boolean'].includes(typeof innerValue)
      )
      .map(([key, innerValue]) => `${key}: ${innerValue}`)
      .join(' · ');
  }

  return '';
}

function normalizeAlertList(alerts, keyPrefix) {
  if (!Array.isArray(alerts)) {
    return [];
  }

  return alerts
    .map((alert, index) => normalizeAlertItem(alert, index, keyPrefix))
    .filter(Boolean);
}

function normalizeAlertItem(rawAlert, index, keyPrefix) {
  if (typeof rawAlert === 'string') {
    const title = normalizeString(rawAlert);
    return title
      ? {
          key: `${keyPrefix}-${index}`,
          tone: 'warning',
          category: 'general',
          title,
          message: '',
          details: [],
          bookingId: null,
        }
      : null;
  }

  if (!rawAlert || typeof rawAlert !== 'object') {
    return null;
  }

  const title =
    normalizeString(rawAlert.title) ||
    normalizeString(rawAlert.label) ||
    normalizeString(rawAlert.type) ||
    t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.ITEM_TITLE');

  return {
    key: normalizeString(rawAlert.key) || `${keyPrefix}-${index}`,
    tone: normalizeTone(
      rawAlert.tone ?? rawAlert.severity ?? rawAlert.level ?? rawAlert.status
    ),
    category:
      normalizeString(rawAlert.category ?? rawAlert.type ?? rawAlert.kind) ||
      'general',
    title,
    message:
      normalizeString(
        rawAlert.message ?? rawAlert.summary ?? rawAlert.description
      ) || '',
    details: normalizeStringArray(rawAlert.details ?? rawAlert.reasons),
    bookingId:
      normalizeNumber(
        rawAlert.booking_id ?? rawAlert.bookingId ?? rawAlert.booking
      ) || null,
  };
}

function bookingDisplayName(booking) {
  const bookingId = booking.bookingId ? `#${booking.bookingId}` : '';
  const tour = normalizeString(booking.tour);

  return [bookingId, tour].filter(Boolean).join(' · ') ||
    t('CONTACT_PANEL.ACTION_CENTER.BOOKING_CARD.UNKNOWN_TOUR');
}

function isCancelledBooking(booking) {
  return normalizeBookingStatusValue(booking.status) === 'cancelled';
}

function parseDateOnly(dateValue) {
  const normalized = normalizeString(dateValue);
  if (!normalized) {
    return null;
  }

  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/u);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function daysUntilDate(dateValue) {
  const date = parseDateOnly(dateValue);
  if (!date) {
    return null;
  }

  const today = new Date();
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  return Math.round((date.getTime() - todayOnly.getTime()) / 86400000);
}

function logisticsDistanceLabel(daysUntil) {
  if (daysUntil === 0) {
    return t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.TODAY');
  }

  if (daysUntil === 1) {
    return t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.TOMORROW');
  }

  return '';
}

function buildLocalConversationAlerts(bookings) {
  const alerts = [];

  bookings.forEach(booking => {
    if (isCancelledBooking(booking)) {
      return;
    }

    const bookingName = bookingDisplayName(booking);
    const daysUntil = daysUntilDate(booking.startDate);
    const isTodayOrTomorrow = daysUntil === 0 || daysUntil === 1;
    const paymentStatus = normalizeString(booking.paymentStatus).toLowerCase();
    const amountDue = normalizeMoney(booking.amountDue);
    const paidAmount = normalizeMoney(booking.paidAmount);
    const hasAmountDue = amountDue !== null && amountDue > 0;
    const isUnpaid = [
      'pending',
      'unpaid',
      'not_paid',
      'awaiting_payment',
    ].includes(paymentStatus);
    const isPartial =
      ['partial', 'deposit'].includes(paymentStatus) ||
      (hasAmountDue && paidAmount !== null && paidAmount > 0);

    if (isUnpaid || isPartial || hasAmountDue) {
      alerts.push({
        key: `payment-${booking.key}`,
        tone: isTodayOrTomorrow && (isUnpaid || hasAmountDue) ? 'danger' : 'warning',
        category: 'payment',
        title: t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.PAYMENT_TITLE', {
          booking: bookingName,
        }),
        message: t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.PAYMENT_MESSAGE', {
          status: paymentStatusLabel(booking.paymentStatus),
          amount: hasAmountDue
            ? formatMoney(amountDue)
            : t('CONTACT_PANEL.ACTION_CENTER.UNKNOWN_SHORT'),
        }),
        details: [],
        bookingId: booking.bookingId,
      });
    }

    if (booking.pickupAssigned !== true) {
      const hasDraft = normalizeString(booking.pickupDraftTime);
      alerts.push({
        key: `pickup-${booking.key}`,
        tone: isTodayOrTomorrow ? 'danger' : 'warning',
        category: 'pickup',
        title: t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.PICKUP_TITLE', {
          booking: bookingName,
        }),
        message: hasDraft
          ? t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.PICKUP_DRAFT_MESSAGE', {
              time: booking.pickupDraftTime,
            })
          : t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.PICKUP_MISSING_MESSAGE'),
        details: [],
        bookingId: booking.bookingId,
      });
    }

    const missingItems = Array.isArray(booking.missingItems)
      ? booking.missingItems.filter(Boolean)
      : [];
    const passportMissing = Math.max(
      0,
      Number(booking.passportNeeded || 0) - Number(booking.passportUploaded || 0)
    );
    const docsNeedAttention =
      booking.docsReady === false ||
      missingItems.length > 0 ||
      passportMissing > 0 ||
      (booking.passportRequired && !booking.docsReady);

    if (docsNeedAttention) {
      alerts.push({
        key: `docs-${booking.key}`,
        tone: isTodayOrTomorrow ? 'danger' : 'warning',
        category: 'docs',
        title: t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.DOCS_TITLE', {
          booking: bookingName,
        }),
        message:
          missingItems.length > 0
            ? t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.DOCS_MISSING_MESSAGE', {
                items: missingItems.join(', '),
              })
            : t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.DOCS_GENERIC_MESSAGE'),
        details:
          passportMissing > 0
            ? [
                t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.PASSPORT_MISSING', {
                  count: passportMissing,
                }),
              ]
            : [],
        bookingId: booking.bookingId,
      });
    }

    if (isTodayOrTomorrow) {
      const logisticsWarnings = [
        booking.pickupAssigned === true ? null : resolvePickupStatus(booking).label,
        docsNeedAttention ? resolveDocsStatus(booking).label : null,
        isUnpaid || isPartial || hasAmountDue
          ? paymentStatusLabel(booking.paymentStatus)
          : null,
        normalizeString(booking.guideName)
          ? null
          : t('CONTACT_PANEL.ACTION_CENTER.BOOKING_CARD.GUIDE_UNASSIGNED'),
      ].filter(Boolean);

      alerts.push({
        key: `logistics-${booking.key}`,
        tone: logisticsWarnings.length ? 'warning' : 'brand',
        category: 'logistics',
        title: t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.LOGISTICS_TITLE', {
          when: logisticsDistanceLabel(daysUntil),
          booking: bookingName,
        }),
        message:
          logisticsWarnings.join(' · ') ||
          t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.LOGISTICS_READY'),
        details: normalizeString(booking.hotelName) ? [booking.hotelName] : [],
        bookingId: booking.bookingId,
      });
    }
  });

  return alerts;
}

function normalizeSuggestionList(suggestions, keyPrefix) {
  if (!Array.isArray(suggestions)) {
    return [];
  }

  return suggestions
    .map((suggestion, index) =>
      normalizeSuggestionItem(suggestion, index, keyPrefix)
    )
    .filter(Boolean);
}

function normalizeSuggestionItem(rawSuggestion, index, keyPrefix) {
  if (typeof rawSuggestion === 'string') {
    const title = normalizeString(rawSuggestion);
    return title
      ? {
          key: `${keyPrefix}-${index}`,
          serviceTitle: title,
          serviceSlug: '',
          serviceId: null,
          availability: null,
          price: null,
          warnings: [],
          reasons: [],
          draftText: '',
        }
      : null;
  }

  if (!rawSuggestion || typeof rawSuggestion !== 'object') {
    return null;
  }

  const service =
    rawSuggestion.service && typeof rawSuggestion.service === 'object'
      ? rawSuggestion.service
      : {};
  const serviceTitle =
    normalizeString(
      service.title ??
        service.name ??
        rawSuggestion.service_title ??
        rawSuggestion.title ??
        rawSuggestion.name ??
        rawSuggestion.service
    ) || t('CONTACT_PANEL.ACTION_CENTER.TOUR_SUGGESTIONS.UNKNOWN_SERVICE');

  return {
    key:
      normalizeString(rawSuggestion.key) ||
      normalizeString(service.slug) ||
      `${keyPrefix}-${index}`,
    serviceTitle,
    serviceSlug:
      normalizeString(service.slug ?? rawSuggestion.service_slug ?? rawSuggestion.slug) ||
      '',
    serviceId:
      normalizeNumber(service.id ?? rawSuggestion.service_id ?? rawSuggestion.id) ||
      null,
    availability:
      rawSuggestion.availability ??
      rawSuggestion.availability_status ??
      rawSuggestion.slots ??
      null,
    price: rawSuggestion.price ?? rawSuggestion.price_text ?? null,
    warnings: normalizeStringArray(rawSuggestion.warnings ?? rawSuggestion.warning),
    reasons: normalizeStringArray(rawSuggestion.reasons ?? rawSuggestion.reason),
    draftText:
      normalizeString(rawSuggestion.draft_text ?? rawSuggestion.draftText) || '',
  };
}

function formatSuggestionAvailability(suggestion) {
  const availability = suggestion.availability;
  if (availability === null || availability === undefined) {
    return t('CONTACT_PANEL.ACTION_CENTER.UNKNOWN_SHORT');
  }

  if (typeof availability === 'string' || typeof availability === 'number') {
    return normalizeString(availability);
  }

  if (typeof availability === 'object') {
    return [
      normalizeString(availability.label ?? availability.status),
      normalizeString(availability.date),
      normalizeString(availability.slots_left ?? availability.seats_left)
        ? t('CONTACT_PANEL.ACTION_CENTER.TOUR_SUGGESTIONS.SEATS_LEFT', {
            count: availability.slots_left ?? availability.seats_left,
          })
        : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  return t('CONTACT_PANEL.ACTION_CENTER.UNKNOWN_SHORT');
}

function formatSuggestionPrice(suggestion) {
  const price = suggestion.price;
  if (price === null || price === undefined) {
    return t('CONTACT_PANEL.ACTION_CENTER.UNKNOWN_SHORT');
  }

  if (typeof price === 'string' || typeof price === 'number') {
    return normalizeString(price);
  }

  if (typeof price === 'object') {
    const amount =
      price.total ??
      price.amount ??
      price.value ??
      price.price ??
      price.adult_price ??
      null;
    const currency = normalizeString(price.currency) || 'USD';

    return amount === null || amount === undefined
      ? t('CONTACT_PANEL.ACTION_CENTER.UNKNOWN_SHORT')
      : `${amount} ${currency}`;
  }

  return t('CONTACT_PANEL.ACTION_CENTER.UNKNOWN_SHORT');
}

function bookingContractPayload(booking) {
  return compactValue({
    booking_id: booking.bookingId,
    service_id: booking.serviceId,
    tour: booking.tour,
    date: booking.startDate,
    status: booking.status,
    payment_status: booking.paymentStatus,
    total_price: booking.totalPrice,
    paid_amount: booking.paidAmount,
    amount_due: booking.amountDue,
    hotel_name: booking.hotelName,
    guide_name: booking.guideName,
    pickup_time: booking.pickupTime,
    pickup_published_time: booking.pickupPublishedTime,
    pickup_draft_time: booking.pickupDraftTime,
    pickup_assigned: booking.pickupAssigned,
    docs_ready: booking.docsReady,
    missing_items: booking.missingItems,
    guests: booking.guests,
  });
}

function buildAutomationContractPayload(action, extra = {}) {
  const endpointContract = {
    contact_context: 'POST /api/chatwoot/automation/contact-context',
    conversation_alerts: `POST ${conversationAlertsEndpoint}`,
    tomorrow_pickup_draft: `POST ${tomorrowPickupDraftEndpoint}`,
    tour_suggestions: `POST ${tourSuggestionsEndpoint}`,
  }[action];

  return buildBasePayload(action, {
    active_bookings: normalizedBookings.value.map(bookingContractPayload),
    active_booking_ids: bookingIdsLabel.value,
    all_booking_ids: allBookingIdsLabel.value,
    active_booking_summary_lines: props.activeBookingSummaryLines,
    endpoint_contract: endpointContract || `POST ${conversationAlertsEndpoint}`,
    ...extra,
  });
}

function buildTourSuggestionInput() {
  return compactValue({
    date_from: normalizeString(tourSuggestionForm.dateFrom) || null,
    date_to: normalizeString(tourSuggestionForm.dateTo) || null,
    guests: buildGuestsPayload(tourSuggestionForm),
    adults: normalizeNumber(tourSuggestionForm.adults) ?? 0,
    children: normalizeNumber(tourSuggestionForm.children) ?? 0,
    infants: normalizeNumber(tourSuggestionForm.infants) ?? 0,
    hotel_name: normalizeString(tourSuggestionForm.hotelName) || null,
    interest: normalizeString(tourSuggestionForm.interest) || null,
  });
}

function buildAutomationRequestId(prefix) {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function prepareDraftForRichEditor(content) {
  return normalizeString(content)
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(line => {
      const trimmedRight = line.replace(/[ \t]+$/u, '');
      return trimmedRight ? `${trimmedRight}  ` : '';
    })
    .join('\n');
}

function insertDraftIntoReplyEditor(draft) {
  const content = prepareDraftForRichEditor(draft);
  if (!content) {
    return;
  }

  emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, content);
}

function contentAttributesForMessage(message) {
  return message?.content_attributes || message?.contentAttributes || {};
}

function parseMaybeJson(value) {
  const parsed = parsePossibleJson(value);
  return parsed === null ? value : parsed;
}

function extractJsonObjectFromContent(content) {
  const normalized = normalizeString(content);
  if (!normalized) {
    return null;
  }

  const fencedMatch = normalized.match(/```json\s*([\s\S]*?)```/iu);
  if (fencedMatch?.[1]) {
    return parsePossibleJson(fencedMatch[1]);
  }

  const firstBrace = normalized.indexOf('{');
  const lastBrace = normalized.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace > firstBrace) {
    return parsePossibleJson(normalized.slice(firstBrace, lastBrace + 1));
  }

  return null;
}

function automationActionForMessage(message) {
  const contentAttributes = contentAttributesForMessage(message);

  return normalizeString(
    contentAttributes.automation_action ??
      contentAttributes.automationAction ??
      contentAttributes.operator_workflow_command ??
      contentAttributes.operatorWorkflowCommand ??
      contentAttributes.action ??
      ''
  );
}

function automationRequestIdForMessage(message) {
  const contentAttributes = contentAttributesForMessage(message);

  return normalizeString(
    contentAttributes.action_center_request_id ??
      contentAttributes.actionCenterRequestId ??
      contentAttributes.automation_request_id ??
      contentAttributes.automationRequestId ??
      contentAttributes.operator_panel_request_id ??
      contentAttributes.operatorPanelRequestId ??
      contentAttributes.request_id ??
      contentAttributes.requestId ??
      ''
  );
}

function automationResultStatusForMessage(message) {
  const contentAttributes = contentAttributesForMessage(message);

  return normalizeString(
    contentAttributes.operator_workflow_result ??
      contentAttributes.operatorWorkflowResult ??
      contentAttributes.automation_result ??
      contentAttributes.automationResult ??
      ''
  );
}

function isAutomationResultMessage(message, action, requestId, triggerMessageId) {
  if (!message) {
    return false;
  }

  if (
    triggerMessageId &&
    normalizeString(message.id) === normalizeString(triggerMessageId)
  ) {
    return false;
  }

  const contentAttributes = contentAttributesForMessage(message);
  const sourceModule = normalizeString(
    contentAttributes.source_module ?? contentAttributes.sourceModule
  );
  const messageRequestId = automationRequestIdForMessage(message);
  const messageAction = automationActionForMessage(message);
  const resultStatus = automationResultStatusForMessage(message);
  const hasResultMarker = Boolean(
    sourceModule === 'chatwoot_operator_workflows' ||
      resultStatus ||
      contentAttributes.action_center_result ||
      contentAttributes.actionCenterResult ||
      contentAttributes.automation_result ||
      contentAttributes.automationResult ||
      contentAttributes.conversation_alerts ||
      contentAttributes.conversationAlerts ||
      contentAttributes.tour_suggestions ||
      contentAttributes.tourSuggestions
  );
  const expectedActions = new Set([
    action,
    action.replaceAll('_', '-'),
    action.replaceAll('-', '_'),
  ]);

  if (messageRequestId && messageRequestId === requestId) {
    return hasResultMarker;
  }

  if (messageAction && expectedActions.has(messageAction)) {
    return hasResultMarker;
  }

  return (
    sourceModule === 'chatwoot_operator_workflows' &&
    resultStatus !== '' &&
    (!messageAction || expectedActions.has(messageAction))
  );
}

function findAutomationResultMessage(messages, action, requestId, triggerMessageId) {
  return [...messages]
    .reverse()
    .find(message =>
      isAutomationResultMessage(message, action, requestId, triggerMessageId)
    );
}

function extractAutomationResultData(message) {
  const contentAttributes = contentAttributesForMessage(message);
  const contentJson = extractJsonObjectFromContent(message?.content);
  const resultPayload = parseMaybeJson(
    contentAttributes.action_center_result ??
      contentAttributes.actionCenterResult ??
      contentAttributes.automation_payload ??
      contentAttributes.automationPayload ??
      contentAttributes.result_payload ??
      contentAttributes.resultPayload ??
      contentAttributes.payload
  );
  const structured =
    resultPayload && typeof resultPayload === 'object' ? resultPayload : {};
  const contentStructured =
    contentJson && typeof contentJson === 'object' ? contentJson : {};
  const okValue =
    contentAttributes.ok ?? structured.ok ?? contentStructured.ok ?? null;
  const normalizedOk = normalizeBoolean(okValue);
  const draftText = parseMaybeJson(
    contentAttributes.action_center_draft_text ??
      contentAttributes.actionCenterDraftText ??
      structured.draft_text ??
      structured.draftText ??
      contentStructured.draft_text ??
      contentStructured.draftText ??
      contentAttributes.draft_text ??
      contentAttributes.draftText
  );

  return {
    ok:
      normalizedOk ??
      (automationResultStatusForMessage(message) !== 'execution_error' &&
        automationResultStatusForMessage(message) !== 'validation_error' &&
        automationResultStatusForMessage(message) !== 'invalid_command' &&
        automationResultStatusForMessage(message) !== 'action_failed'),
    alerts: parseMaybeJson(
      contentAttributes.alerts ??
        structured.alerts ??
        contentStructured.alerts ??
        contentAttributes.conversation_alerts ??
        contentAttributes.conversationAlerts ??
        contentAttributes.tour_suggestions_alerts ??
        contentAttributes.tourSuggestionsAlerts
    ),
    suggestions: parseMaybeJson(
      contentAttributes.suggestions ??
        structured.suggestions ??
        contentStructured.suggestions ??
        contentAttributes.tour_suggestions ??
        contentAttributes.tourSuggestions
    ),
    summary:
      parseMaybeJson(
        contentAttributes.summary ??
          structured.summary ??
          contentStructured.summary ??
          contentAttributes.conversation_alert_summary ??
          contentAttributes.conversationAlertSummary ??
          contentAttributes.tour_suggestions_summary ??
          contentAttributes.tourSuggestionsSummary
      ) || '',
    input:
      parseMaybeJson(
        contentAttributes.input ??
          structured.input ??
          contentStructured.input ??
          contentAttributes.tour_suggestions_input ??
          contentAttributes.tourSuggestionsInput
      ) || null,
    draftText:
      typeof draftText === 'string' ? normalizeString(draftText) : '',
    message: normalizeString(message?.content),
    resultStatus: automationResultStatusForMessage(message),
  };
}

async function sleep(ms) {
  return new Promise(resolve => {
    window.setTimeout(resolve, ms);
  });
}

async function pollAutomationResult(action, requestId, triggerMessageId) {
  const maxAttempts = 40;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const fromStore = findAutomationResultMessage(
      selectedChatMessages.value,
      action,
      requestId,
      triggerMessageId
    );
    if (fromStore) {
      return extractAutomationResultData(fromStore);
    }

    const { data } = await MessageApi.getPreviousMessages({
      conversationId: props.conversationId,
      after: triggerMessageId || undefined,
    });
    const payload = Array.isArray(data?.payload) ? data.payload : [];
    const fromApi = findAutomationResultMessage(
      payload,
      action,
      requestId,
      triggerMessageId
    );

    if (fromApi) {
      return extractAutomationResultData(fromApi);
    }

    await sleep(1500);
  }

  throw new Error('automation_result_timeout');
}

function currentConversationLabelTitles() {
  const getter = store.getters?.['conversationLabels/getConversationLabels'];
  if (typeof getter !== 'function') {
    return [];
  }

  const labels = getter(props.conversationId);
  return Array.isArray(labels)
    ? labels.map(label => normalizeString(label)).filter(Boolean)
    : [];
}

async function syncVietvedConversationLabel(label) {
  const normalizedLabel = normalizeString(label);
  if (!normalizedLabel) {
    return;
  }

  await store.dispatch('conversationLabels/get', props.conversationId);

  const nextLabels = Array.from(
    new Set([
      ...currentConversationLabelTitles().filter(
        item =>
          !vietvedLifecycleLabelSet.has(item) &&
          !item.startsWith(vietvedLifecycleLabelPrefix)
      ),
      normalizedLabel,
    ])
  );

  await store.dispatch('conversationLabels/update', {
    conversationId: props.conversationId,
    labels: nextLabels,
  });

  if (!currentConversationLabelTitles().includes(normalizedLabel)) {
    throw new Error('conversation_label_sync_failed');
  }
}

async function sendStructuredAutomationRequest({
  action,
  actionKey,
  title,
  summaryLines = [],
  payload,
}) {
  if (sendingKey.value) {
    return null;
  }

  const requestId = buildAutomationRequestId(action);
  const command = `/dv ${action.replaceAll('_', '-')}`;
  const actionPayload = compactValue({
    ...payload,
    action,
    action_center_request_id: requestId,
    operator_panel_request_id: requestId,
    report_private_note: true,
  });
  const noteContent = [title, ...summaryLines.filter(line => normalizeString(line))].join(
    '\n'
  );

  sendingKey.value = actionKey;

  try {
    const response = await MessageApi.create({
      conversationId: props.conversationId,
      message: noteContent,
      private: true,
      contentAttributes: compactValue({
        source_module: sourceModule,
        action_center_command: command,
        action_center_request_id: requestId,
        action_center_payload: actionPayload,
      }),
    });

    return {
      requestId,
      triggerMessageId: normalizeString(response?.data?.id ?? response?.id),
    };
  } catch (error) {
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.ALERTS.FAILED'));
    return null;
  } finally {
    sendingKey.value = '';
  }
}

function applyTourSuggestionDefaults() {
  if (!normalizeString(tourSuggestionForm.hotelName)) {
    tourSuggestionForm.hotelName =
      normalizedBookings.value.find(booking => normalizeString(booking.hotelName))
        ?.hotelName || '';
  }
}

function openTourSuggestions() {
  if (activeGlobalAction.value !== 'tour_suggestions') {
    applyTourSuggestionDefaults();
  }

  openGlobalForm('tour_suggestions');
}

async function refreshConversationAlerts() {
  if (automationRequestState.isRefreshingAlerts) {
    return;
  }

  automationRequestState.isRefreshingAlerts = true;
  automationRequestState.alertsError = '';

  try {
    const payload = buildAutomationContractPayload('conversation_alerts');
    const request = await sendStructuredAutomationRequest({
      action: 'conversation_alerts',
      actionKey: 'conversation_alerts',
      title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.conversation_alerts'),
      summaryLines: [
        bookingIdsLabel.value
          ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING_IDS')}: ${bookingIdsLabel.value}`
          : null,
      ],
      payload,
    });

    if (!request) {
      return;
    }

    const response = await pollAutomationResult(
      'conversation_alerts',
      request.requestId,
      request.triggerMessageId
    );
    if (response?.ok === false) {
      throw new Error(response.message || 'conversation_alerts_failed');
    }

    conversationAlertsResponse.value = response;
    automationRequestState.alertsUpdatedAt = new Date().toLocaleTimeString(
      locale.value || 'ru',
      {
        hour: '2-digit',
        minute: '2-digit',
      }
    );
  } catch (error) {
    automationRequestState.alertsError = t(
      'CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.POLL_FALLBACK'
    );
    conversationAlertsResponse.value = null;
  } finally {
    automationRequestState.isRefreshingAlerts = false;
  }
}

async function requestTomorrowPickupDraft() {
  if (automationRequestState.isLoadingPickupDraft) {
    return;
  }

  const booking = tomorrowPickupBooking.value;
  automationRequestState.isLoadingPickupDraft = true;

  try {
    const payload = buildAutomationContractPayload('tomorrow_pickup_draft', {
      booking_id: booking?.bookingId || null,
      report_private_note: true,
    });
    const request = await sendStructuredAutomationRequest({
      action: 'tomorrow_pickup_draft',
      actionKey: 'tomorrow_pickup_draft',
      title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.tomorrow_pickup_draft', {
        bookingId: booking?.bookingId || 'auto',
      }),
      summaryLines: [
        booking?.bookingId
          ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING')}: #${booking.bookingId}`
          : t('CONTACT_PANEL.ACTION_CENTER.TOMORROW_PICKUP.DISCOVER'),
        normalizeString(booking?.tour)
          ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.SERVICE')}: ${booking.tour}`
          : null,
        normalizeString(booking?.pickupPublishedTime || booking?.pickupTime)
          ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.PICKUP_TIME')}: ${
              booking.pickupPublishedTime || booking.pickupTime
            }`
          : null,
      ],
      payload,
    });

    if (!request) {
      return;
    }

    const response = await pollAutomationResult(
      'tomorrow_pickup_draft',
      request.requestId,
      request.triggerMessageId
    );
    if (response?.ok === false) {
      throw new Error(response.message || 'tomorrow_pickup_draft_failed');
    }

    const draft = normalizeString(response?.draftText);
    if (!draft) {
      useAlert(t('CONTACT_PANEL.ACTION_CENTER.TOMORROW_PICKUP.NO_DRAFT'));
      return;
    }

    insertDraftIntoReplyEditor(draft);
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.TOMORROW_PICKUP.DRAFT_INSERTED'));
  } catch (error) {
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.ALERTS.FAILED'));
  } finally {
    automationRequestState.isLoadingPickupDraft = false;
  }
}

async function requestTourSuggestions() {
  if (automationRequestState.isLoadingSuggestions) {
    return;
  }

  const input = buildTourSuggestionInput();
  if (
    !normalizeString(input?.date_from) &&
    !normalizeString(input?.date_to) &&
    !normalizeString(input?.hotel_name) &&
    !normalizeString(input?.interest)
  ) {
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.ALERTS.TOUR_INPUT_REQUIRED'));
    return;
  }

  automationRequestState.isLoadingSuggestions = true;
  automationRequestState.suggestionsError = '';
  tourSuggestionInputEcho.value = input;

  try {
    const payload = buildAutomationContractPayload('tour_suggestions', {
      input,
      query: input.interest || null,
      date: input.date_from || null,
      from_date: input.date_from || null,
      to_date: input.date_to || null,
      hotel_name: input.hotel_name || null,
      guests: input.guests || null,
      adults: input.adults ?? null,
      children: input.children ?? null,
      infants: input.infants ?? null,
      limit: 6,
    });
    const request = await sendStructuredAutomationRequest({
      action: 'tour_suggestions',
      actionKey: 'tour_suggestions',
      title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.tour_suggestions'),
      summaryLines: [
        input.date_from
          ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.DATE_FROM')}: ${input.date_from}`
          : null,
        input.date_to
          ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.DATE_TO')}: ${input.date_to}`
          : null,
        input.hotel_name
          ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.HOTEL')}: ${input.hotel_name}`
          : null,
        input.interest
          ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.INTEREST')}: ${input.interest}`
          : null,
      ],
      payload,
    });

    if (!request) {
      return;
    }

    const response = await pollAutomationResult(
      'tour_suggestions',
      request.requestId,
      request.triggerMessageId
    );
    if (response?.ok === false) {
      throw new Error(response.message || 'tour_suggestions_failed');
    }

    tourSuggestionResults.value = normalizeSuggestionList(
      response.suggestions,
      'remote-suggestion'
    );
    conversationAlertsResponse.value =
      Array.isArray(response.alerts) && response.alerts.length
        ? { alerts: response.alerts, summary: response.summary || '' }
        : conversationAlertsResponse.value;
    automationRequestState.suggestionsUpdatedAt = new Date().toLocaleTimeString(
      locale.value || 'ru',
      {
        hour: '2-digit',
        minute: '2-digit',
      }
    );
  } catch (error) {
    tourSuggestionResults.value = [];
    automationRequestState.suggestionsError = t(
      'CONTACT_PANEL.ACTION_CENTER.TOUR_SUGGESTIONS.POLL_FALLBACK'
    );
  } finally {
    automationRequestState.isLoadingSuggestions = false;
  }
}

function insertSuggestionDraft(suggestion) {
  const draft = normalizeString(suggestion?.draftText);
  if (!draft) {
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.TOUR_SUGGESTIONS.NO_DRAFT'));
    return;
  }

  insertDraftIntoReplyEditor(draft);
  useAlert(t('CONTACT_PANEL.ACTION_CENTER.TOUR_SUGGESTIONS.DRAFT_INSERTED'));
}

function insertVietvedLifecycleDraft(stage) {
  const draft = normalizeString(stage?.draftText);
  if (!draft) {
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.NO_DRAFT'));
    return;
  }

  insertDraftIntoReplyEditor(draft);
  useAlert(t('CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.DRAFT_INSERTED'));
}

async function sendVietvedLifecycle(stage) {
  if (!stage?.key) {
    return;
  }

  const payload = buildBasePayload('vietved_lifecycle', {
    stage: stage.key,
    note: normalizeString(vietvedLifecycleNote.value) || null,
    action_key: stage.key,
    draft_text: stage.draftText,
    report_private_note: true,
  });

  try {
    const request = await sendStructuredAutomationRequest({
      action: 'vietved_lifecycle',
      actionKey: `vietved_lifecycle:${stage.key}`,
      title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.vietved_lifecycle', {
        stage: stage.label,
      }),
      summaryLines: [
        `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.STATUS')}: ${stage.label}`,
        `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.ACTION')}: ${stage.actionTitle}`,
        `${t('CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.FILTER_LABEL')}: #${stage.conversationLabel}`,
        normalizeString(vietvedLifecycleNote.value)
          ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.NOTE')}: ${vietvedLifecycleNote.value}`
          : null,
      ],
      payload,
    });

    if (!request) {
      return;
    }

    await syncVietvedConversationLabel(stage.conversationLabel);
    localVietvedLifecycleStage.value = stage.key;

    const response = await pollAutomationResult(
      'vietved_lifecycle',
      request.requestId,
      request.triggerMessageId
    );
    if (response?.ok === false) {
      throw new Error(response.message || 'vietved_lifecycle_failed');
    }

    vietvedLifecycleNote.value = '';
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.ALERTS.SENT'));
  } catch (error) {
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.ALERTS.FAILED'));
  }
}

function badgeClasses(tone = 'neutral') {
  return {
    'inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium': true,
    'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-600/40 dark:bg-emerald-500/10 dark:text-emerald-200':
      tone === 'success',
    'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-600/40 dark:bg-rose-500/10 dark:text-rose-200':
      tone === 'danger',
    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-600/40 dark:bg-amber-500/10 dark:text-amber-200':
      tone === 'warning',
    'border-n-brand/20 bg-n-brand/10 text-n-brand':
      tone === 'brand',
    'border-n-weak bg-n-alpha-2 text-n-slate-11 dark:border-n-weak/80 dark:bg-n-slate-2 dark:text-n-slate-11':
      tone === 'neutral',
  };
}

function actionButtonClasses({ active = false, danger = false } = {}) {
  return {
    'inline-flex min-h-10 min-w-0 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-center text-xs font-medium transition':
      true,
    'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-600/40 dark:bg-rose-500/10 dark:text-rose-200':
      danger,
    'border-n-brand/20 bg-n-brand/10 text-n-brand hover:bg-n-brand/15':
      active && !danger,
    'border-n-weak bg-white text-n-slate-12 hover:bg-n-alpha-2 dark:border-n-weak/80 dark:bg-n-slate-1 dark:text-n-slate-12 dark:hover:bg-n-slate-2':
      !active && !danger,
  };
}

function primaryActionButtonClasses() {
  return 'inline-flex w-full min-w-0 items-center justify-center gap-3 rounded-xl border border-n-brand bg-n-brand px-4 py-3 text-left text-white shadow-sm transition hover:bg-n-brand/90 focus:outline-none focus:ring-2 focus:ring-n-brand/30 disabled:cursor-not-allowed disabled:opacity-60';
}

function pickupDraftButtonClasses() {
  return 'mt-3 inline-flex w-full min-w-0 items-center justify-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-left text-emerald-700 transition hover:bg-emerald-500/15 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-100 dark:hover:bg-emerald-400/15';
}

function inputClasses() {
  return 'min-h-10 w-full rounded-lg border border-n-weak bg-white px-3 py-2 text-sm text-n-slate-12 outline-none transition focus:border-n-brand focus:ring-2 focus:ring-n-brand/10 dark:border-n-weak/80 dark:bg-n-slate-1 dark:text-n-slate-12';
}

function textareaClasses() {
  return `${inputClasses()} min-h-[76px] resize-y`;
}

async function dispatchActionNote({
  actionKey,
  command,
  title,
  summaryLines = [],
  payload,
  afterSuccess = null,
}) {
  if (sendingKey.value) {
    return;
  }

  sendingKey.value = actionKey;

  const noteContent = [title, ...summaryLines.filter(line => normalizeString(line))].join(
    '\n'
  );

  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: props.conversationId,
      message: noteContent,
      private: true,
      contentAttributes: compactValue({
        source_module: sourceModule,
        action_center_command: normalizeString(payload?.action) || command,
        action_center_payload: payload,
      }),
    });

    useAlert(t('CONTACT_PANEL.ACTION_CENTER.ALERTS.SENT'));

    if (typeof afterSuccess === 'function') {
      afterSuccess();
    }
  } catch (error) {
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.ALERTS.FAILED'));
  } finally {
    sendingKey.value = '';
  }
}

async function sendRefreshContext() {
  const payload = buildAutomationContractPayload('contact_context', {
    include_closed: true,
    report_private_note: true,
  });

  await dispatchActionNote({
    actionKey: 'contact_context',
    command: '/dv contact-context',
    title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.contact_context'),
    summaryLines: [
      defaultContactName.value
        ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.CONTACT')}: ${defaultContactName.value}`
        : null,
      bookingIdsLabel.value
        ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING_IDS')}: ${bookingIdsLabel.value}`
        : null,
    ],
    payload,
  });
}

async function sendSingleBooking() {
  const service = resolveServiceSelection(singleBookingForm.service);
  if (!service) {
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.ALERTS.SERVICE_REQUIRED'));
    return;
  }

  if (!normalizeString(singleBookingForm.date)) {
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.ALERTS.DATE_REQUIRED'));
    return;
  }

  const guests = buildGuestsPayload(singleBookingForm);
  const contactName = resolveFilledValue(
    singleBookingForm.contactName,
    defaultContactName.value
  );
  const contactPhone = resolveFilledValue(
    singleBookingForm.contactPhone,
    defaultContactPhone.value
  );
  const contactEmail = resolveFilledValue(
    singleBookingForm.contactEmail,
    defaultContactEmail.value
  );
  const payload = buildBasePayload('booking_agent', {
    ...service,
    date: singleBookingForm.date,
    booking_type: normalizeString(singleBookingForm.bookingType) || 'ticket',
    guests,
    adults: guests?.adult ?? undefined,
    children: guests?.child ?? undefined,
    infants: guests?.infant ?? undefined,
    hotel_name: normalizeString(singleBookingForm.hotelName) || null,
    pickup_time: normalizeString(singleBookingForm.pickupTime) || null,
    contact_name: contactName,
    contact_phone: contactPhone,
    contact_email: contactEmail,
    workflow_note: normalizeString(singleBookingForm.note) || null,
  });

  await dispatchActionNote({
    actionKey: 'booking_agent',
    command: '/dv booking-agent',
    title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.booking_agent'),
    summaryLines: [
      `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.SERVICE')}: ${service.service_label}`,
      `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.DATE')}: ${singleBookingForm.date}`,
      `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.GUESTS')}: ${formatGuests(guests)}`,
      normalizeString(singleBookingForm.hotelName)
        ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.HOTEL')}: ${singleBookingForm.hotelName}`
        : null,
      contactPhone
        ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.PHONE')}: ${contactPhone}`
        : null,
      contactEmail
        ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.EMAIL')}: ${contactEmail}`
        : null,
    ],
    payload,
    afterSuccess: () => {
      activeGlobalAction.value = '';
      Object.assign(singleBookingForm, {
        service: '',
        date: '',
        bookingType: 'ticket',
        adults: 1,
        children: 0,
        infants: 0,
        hotelName: '',
        pickupTime: '',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        note: '',
      });
    },
  });
}

function validateManyBookingRows() {
  const preparedRows = [];

  for (const [index, row] of manyBookingRows.value.entries()) {
    const hasAnyValue = [
      row.service,
      row.date,
      row.hotelName,
      row.note,
      row.pickupTime,
      normalizeNumber(row.adults) !== 1 ? row.adults : '',
      normalizeNumber(row.children) !== 0 ? row.children : '',
      normalizeNumber(row.infants) !== 0 ? row.infants : '',
    ].some(value => normalizeString(value) !== '');

    if (!hasAnyValue) {
      continue;
    }

    const service = resolveServiceSelection(row.service);
    if (!service) {
      useAlert(
        t('CONTACT_PANEL.ACTION_CENTER.ALERTS.MANY_ROW_SERVICE_REQUIRED', {
          index: index + 1,
        })
      );
      return null;
    }

    if (!normalizeString(row.date)) {
      useAlert(
        t('CONTACT_PANEL.ACTION_CENTER.ALERTS.MANY_ROW_DATE_REQUIRED', {
          index: index + 1,
        })
      );
      return null;
    }

    preparedRows.push({
      ...row,
      service,
    });
  }

  if (!preparedRows.length) {
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.ALERTS.MANY_ROWS_REQUIRED'));
    return null;
  }

  return preparedRows;
}

async function sendManyBookings() {
  const rows = validateManyBookingRows();
  if (!rows) {
    return;
  }

  const contactName = resolveFilledValue(
    manyBookingCommonForm.contactName,
    defaultContactName.value
  );
  const contactPhone = resolveFilledValue(
    manyBookingCommonForm.contactPhone,
    defaultContactPhone.value
  );
  const contactEmail = resolveFilledValue(
    manyBookingCommonForm.contactEmail,
    defaultContactEmail.value
  );

  const bookings = rows.map(row => {
    const guests = buildGuestsPayload(row);

    return compactValue({
      ...row.service,
      date: row.date,
      booking_type: normalizeString(row.bookingType) || 'ticket',
      guests,
      adults: guests?.adult ?? undefined,
      children: guests?.child ?? undefined,
      infants: guests?.infant ?? undefined,
      hotel_name: normalizeString(row.hotelName) || null,
      pickup_time: normalizeString(row.pickupTime) || null,
      workflow_note: normalizeString(row.note) || null,
    });
  });

  const payload = buildBasePayload('many_booking_agent', {
    contact_name: contactName,
    contact_phone: contactPhone,
    contact_email: contactEmail,
    manager_note: normalizeString(manyBookingCommonForm.managerNote) || null,
    bookings,
  });

  await dispatchActionNote({
    actionKey: 'many_booking_agent',
    command: '/dv many-booking-agent',
    title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.many_booking_agent'),
    summaryLines: [
      `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING_COUNT')}: ${bookings.length}`,
      ...bookings.map((booking, index) =>
        `${index + 1}. ${booking.service_slug || booking.service_query || `#${booking.service_id}`} · ${booking.date} · ${formatGuests(booking.guests)}`
      ),
    ],
    payload,
    afterSuccess: () => {
      activeGlobalAction.value = '';
      Object.assign(manyBookingCommonForm, {
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        managerNote: '',
      });
      manyBookingRows.value = [createManyBookingRow()];
    },
  });
}

function formatGuests(guests) {
  if (!guests) {
    return t('CONTACT_PANEL.ACTION_CENTER.UNKNOWN_SHORT');
  }

  return `${guests.adult || 0}/${guests.child || 0}/${guests.infant || 0}`;
}

function ensureBookingId(booking) {
  if (booking.bookingId) {
    return true;
  }

  useAlert(t('CONTACT_PANEL.ACTION_CENTER.ALERTS.BOOKING_ID_REQUIRED'));
  return false;
}

async function sendUpdateBooking(booking) {
  if (!ensureBookingId(booking)) {
    return;
  }

  const form = ensureUpdateForm(booking);
  const guests = buildGuestsPayload(form);
  const payload = buildBasePayload('update_booking', {
    booking_id: booking.bookingId,
    confirmed_update: true,
    confirmation_text: t(
      'CONTACT_PANEL.ACTION_CENTER.CONFIRMATION_TEXTS.update_booking'
    ),
    new_date: normalizeString(form.newDate) || null,
    hotel_name: normalizeString(form.hotelName) || null,
    guests,
  });

  await dispatchActionNote({
    actionKey: bookingActionKey(booking, 'update_booking'),
    command: '/dv update-booking',
    title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.update_booking', {
      bookingId: booking.bookingId,
    }),
    summaryLines: [
      `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING')}: #${booking.bookingId}`,
      normalizeString(form.newDate)
        ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.NEW_DATE')}: ${form.newDate}`
        : null,
      normalizeString(form.hotelName)
        ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.HOTEL')}: ${form.hotelName}`
        : null,
      guests
        ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.GUESTS')}: ${formatGuests(guests)}`
        : null,
    ],
    payload,
    afterSuccess: () => {
      activeBookingAction.value = '';
    },
  });
}

async function sendAssignGuide(booking) {
  if (!ensureBookingId(booking)) {
    return;
  }

  const form = ensureAssignGuideForm(booking);
  const guideText = normalizeString(form.guide);
  const guideId = normalizeNumber(form.guideId);

  if (!guideText && !guideId) {
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.ALERTS.GUIDE_REQUIRED'));
    return;
  }

  const payload = buildBasePayload('assign_guide', {
    booking_id: booking.bookingId,
    date: booking.startDate || null,
    hotel_name: booking.hotelName || null,
    guide_id: guideId,
    guide_name: guideText || null,
    guide_query: guideText || null,
    note: normalizeString(form.note) || null,
  });

  await dispatchActionNote({
    actionKey: bookingActionKey(booking, 'assign_guide'),
    command: '/dv assign-guide',
    title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.assign_guide', {
      bookingId: booking.bookingId,
    }),
    summaryLines: [
      `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING')}: #${booking.bookingId}`,
      guideId
        ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.GUIDE_ID')}: ${guideId}`
        : null,
      guideText
        ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.GUIDE')}: ${guideText}`
        : null,
    ],
    payload,
    afterSuccess: () => {
      activeBookingAction.value = '';
    },
  });
}

async function sendSetBookingStatus(booking) {
  if (!ensureBookingId(booking)) {
    return;
  }

  const form = ensureSetStatusForm(booking);
  const status = normalizeBookingStatusValue(form.status);
  if (!status) {
    useAlert(t('CONTACT_PANEL.ACTION_CENTER.ALERTS.STATUS_REQUIRED'));
    return;
  }

  const payload = buildBasePayload('set_booking_status', {
    booking_id: booking.bookingId,
    date: booking.startDate || null,
    hotel_name: booking.hotelName || null,
    status,
    reason: normalizeString(form.note) || null,
  });

  await dispatchActionNote({
    actionKey: bookingActionKey(booking, 'set_booking_status'),
    command: '/dv set-booking-status',
    title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.set_booking_status', {
      bookingId: booking.bookingId,
    }),
    summaryLines: [
      `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING')}: #${booking.bookingId}`,
      `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.STATUS')}: ${bookingStatusLabel(status)}`,
    ],
    payload,
    afterSuccess: () => {
      activeBookingAction.value = '';
    },
  });
}

async function sendGeneratePaymentQr(booking) {
  if (!ensureBookingId(booking)) {
    return;
  }

  const form = ensureGenerateQrForm(booking);
  const payload = buildBasePayload('generate_payment_qr', {
    booking_id: booking.bookingId,
    date: booking.startDate || null,
    hotel_name: booking.hotelName || null,
    payment_type: normalizeString(form.paymentType) || 'deposit',
    deposit_amount:
      normalizeString(form.depositAmount) && form.paymentType === 'deposit'
        ? normalizeString(form.depositAmount)
        : null,
    workflow_note: normalizeString(form.note) || null,
  });

  await dispatchActionNote({
    actionKey: bookingActionKey(booking, 'generate_payment_qr'),
    command: '/dv generate-payment-qr',
    title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.generate_payment_qr', {
      bookingId: booking.bookingId,
    }),
    summaryLines: [
      `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING')}: #${booking.bookingId}`,
      `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.PAYMENT_TYPE')}: ${t(
        `CONTACT_PANEL.ACTION_CENTER.PAYMENT_TYPE_OPTIONS.${form.paymentType || 'deposit'}`
      )}`,
      normalizeString(form.depositAmount) && form.paymentType === 'deposit'
        ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.DEPOSIT_AMOUNT')}: ${form.depositAmount}`
        : null,
      normalizeString(form.note)
        ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.NOTE')}: ${form.note}`
        : null,
    ],
    payload,
    afterSuccess: () => {
      activeBookingAction.value = '';
    },
  });
}

async function sendQrStatus(booking) {
  if (!ensureBookingId(booking)) {
    return;
  }

  const form = ensureQrStatusForm(booking);
  const payload = buildBasePayload('qr_status', {
    booking_id: booking.bookingId,
    refresh_provider_status: !!form.refreshProviderStatus,
    report_private_note: true,
  });

  await dispatchActionNote({
    actionKey: bookingActionKey(booking, 'qr_status_send'),
    command: '/dv qr-status',
    title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.qr_status', {
      bookingId: booking.bookingId,
    }),
    summaryLines: [
      `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING')}: #${booking.bookingId}`,
      `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.PROVIDER_REFRESH')}: ${form.refreshProviderStatus ? t('CONTACT_PANEL.ACTION_CENTER.YES') : t('CONTACT_PANEL.ACTION_CENTER.NO')}`,
    ],
    payload,
    afterSuccess: () => {
      activeBookingAction.value = '';
    },
  });
}

async function sendPickupStatus(booking) {
  if (!ensureBookingId(booking)) {
    return;
  }

  const payload = buildBasePayload('pickup_status', {
    booking_id: booking.bookingId,
    report_private_note: true,
  });

  await dispatchActionNote({
    actionKey: bookingActionKey(booking, 'pickup_status'),
    command: '/dv pickup-status',
    title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.pickup_status', {
      bookingId: booking.bookingId,
    }),
    summaryLines: [`${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING')}: #${booking.bookingId}`],
    payload,
  });
}

async function sendDocsStatus(booking) {
  if (!ensureBookingId(booking)) {
    return;
  }

  const payload = buildBasePayload('docs_status', {
    booking_id: booking.bookingId,
    report_private_note: true,
  });

  await dispatchActionNote({
    actionKey: bookingActionKey(booking, 'docs_status'),
    command: '/dv docs-status',
    title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.docs_status', {
      bookingId: booking.bookingId,
    }),
    summaryLines: [`${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING')}: #${booking.bookingId}`],
    payload,
  });
}

async function sendCancelBooking(booking) {
  if (!ensureBookingId(booking)) {
    return;
  }

  const form = ensureCancelForm(booking);
  const payload = buildBasePayload('cancel_booking', {
    booking_id: booking.bookingId,
    confirmed_cancel: true,
    confirmation_text: t(
      'CONTACT_PANEL.ACTION_CENTER.CONFIRMATION_TEXTS.cancel_booking'
    ),
    reason: normalizeString(form.reason) || null,
  });

  await dispatchActionNote({
    actionKey: bookingActionKey(booking, 'cancel_booking_send'),
    command: '/dv cancel-booking',
    title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.cancel_booking', {
      bookingId: booking.bookingId,
    }),
    summaryLines: [
      `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING')}: #${booking.bookingId}`,
      normalizeString(form.reason)
        ? `${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.REASON')}: ${form.reason}`
        : null,
    ],
    payload,
    afterSuccess: () => {
      activeBookingAction.value = '';
    },
  });
}

async function sendRestoreBooking(booking) {
  if (!ensureBookingId(booking)) {
    return;
  }

  const payload = buildBasePayload('restore_booking', {
    booking_id: booking.bookingId,
    confirmed_restore: true,
    confirmation_text: t(
      'CONTACT_PANEL.ACTION_CENTER.CONFIRMATION_TEXTS.restore_booking'
    ),
  });

  await dispatchActionNote({
    actionKey: bookingActionKey(booking, 'restore_booking'),
    command: '/dv restore-booking',
    title: t('CONTACT_PANEL.ACTION_CENTER.NOTE_TITLES.restore_booking', {
      bookingId: booking.bookingId,
    }),
    summaryLines: [`${t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING')}: #${booking.bookingId}`],
    payload,
  });
}
</script>

<template>
  <div class="px-2 pb-2">
    <div class="rounded-xl border border-n-weak bg-n-alpha-2 p-3 dark:border-n-weak/80 dark:bg-n-slate-2">
      <div v-if="isDvAccount" class="grid grid-cols-2 gap-2">
        <button
          type="button"
          class="flex min-w-0 flex-col items-start gap-1 rounded-xl bg-n-brand px-3 py-2.5 text-left text-white shadow-sm transition hover:bg-n-brand/90 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="!!sendingKey"
          :title="$t('CONTACT_PANEL.ACTION_CENTER.CLIENT_DOSSIER.DESCRIPTION')"
          @click="sendRefreshContext"
        >
          <span class="i-lucide-user-search text-lg" />
          <span class="text-[13px] font-semibold leading-4">
            {{ $t('CONTACT_PANEL.ACTION_CENTER.DV_TILES.DOSSIER_TITLE') }}
          </span>
          <span class="text-[11px] leading-4 text-white/85">
            {{ $t('CONTACT_PANEL.ACTION_CENTER.DV_TILES.DOSSIER_HINT') }}
          </span>
        </button>
        <button
          type="button"
          class="flex min-w-0 flex-col items-start gap-1 rounded-xl bg-n-teal-3 px-3 py-2.5 text-left text-n-teal-11 transition hover:bg-n-teal-4 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="!!sendingKey || automationRequestState.isLoadingPickupDraft"
          :title="tomorrowPickupSummary"
          @click="requestTomorrowPickupDraft"
        >
          <span class="i-lucide-route text-lg" />
          <span class="text-[13px] font-semibold leading-4">
            {{ $t('CONTACT_PANEL.ACTION_CENTER.DV_TILES.PICKUP_TITLE') }}
          </span>
          <span class="line-clamp-2 text-[11px] leading-4 opacity-90">
            {{ dvPickupTileHint }}
          </span>
        </button>
      </div>
      <template v-else>
      <button
        type="button"
        :class="primaryActionButtonClasses()"
        :disabled="!!sendingKey"
        @click="sendRefreshContext"
      >
        <span class="i-lucide-user-search shrink-0 text-lg" />
        <span class="flex min-w-0 flex-col">
          <span class="text-sm font-semibold leading-5">
            {{ $t('CONTACT_PANEL.ACTION_CENTER.CLIENT_DOSSIER.BUTTON') }}
          </span>
          <span class="mt-1 text-[11px] font-normal leading-4 text-white/90">
            {{ $t('CONTACT_PANEL.ACTION_CENTER.CLIENT_DOSSIER.DESCRIPTION') }}
          </span>
        </span>
      </button>

      <button
        type="button"
        :class="pickupDraftButtonClasses()"
        :disabled="
          !!sendingKey ||
          automationRequestState.isLoadingPickupDraft
        "
        @click="requestTomorrowPickupDraft"
      >
        <span class="i-lucide-route shrink-0 text-lg" />
        <span class="flex min-w-0 flex-col">
          <span class="text-sm font-semibold leading-5">
            {{ $t('CONTACT_PANEL.ACTION_CENTER.TOMORROW_PICKUP.BUTTON') }}
          </span>
          <span class="mt-1 text-[11px] font-normal leading-4 opacity-90">
            {{ tomorrowPickupSummary }}
          </span>
        </span>
      </button>
      </template>

      <div v-if="isDvAccount" class="mt-3 flex items-stretch gap-2">
        <button
          type="button"
          :class="actionButtonClasses()"
          class="min-w-0 flex-1 !justify-start disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="!clientPortalUrl"
          :title="
            clientPortalUrl
              ? $t('CONTACT_PANEL.ACTION_CENTER.CLIENT_PORTAL.OPEN')
              : $t('CONTACT_PANEL.ACTION_CENTER.CLIENT_PORTAL.MISSING')
          "
          @click="openClientPortal"
        >
          <span class="i-lucide-key-round shrink-0 text-sm" />
          <span class="truncate">
            {{ $t('CONTACT_PANEL.ACTION_CENTER.CLIENT_PORTAL.TITLE') }}
          </span>
          <span
            v-if="clientPortalUrl"
            class="i-lucide-external-link ml-auto shrink-0 text-xs opacity-70"
          />
        </button>
        <button
          type="button"
          :class="actionButtonClasses()"
          class="disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="!clientPortalUrl"
          :title="$t('CONTACT_PANEL.ACTION_CENTER.CLIENT_PORTAL.INSERT')"
          @click="insertClientPortalUrl"
        >
          <span class="i-lucide-corner-down-left text-sm" />
        </button>
        <button
          type="button"
          :class="actionButtonClasses()"
          class="disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="!clientPortalUrl"
          :title="$t('CONTACT_PANEL.ACTION_CENTER.CLIENT_PORTAL.COPY')"
          @click="copyClientPortalUrl"
        >
          <span class="i-lucide-copy text-sm" />
        </button>
      </div>

      <template v-if="!isDvAccount">
      <div class="mt-3 flex flex-wrap items-center justify-between gap-3">
        <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
          {{ $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_CONTEXT') }}
        </span>
        <span
          :class="badgeClasses('brand')"
        >
          {{ bookingCountLabel }}
        </span>
      </div>

      <p
        v-if="bookingIdsLabel"
        class="mt-2 text-xs text-n-slate-11 dark:text-n-slate-11"
      >
        {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIVE_IDS') }}: {{ bookingIdsLabel }}
      </p>

      <div class="mt-3 rounded-lg border border-n-weak bg-white p-3 dark:border-n-weak/80 dark:bg-n-slate-1">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.ALERT_BLOCK.TITLE') }}
              </p>
              <span :class="badgeClasses(criticalAlertCount ? 'warning' : 'success')">
                {{ criticalAlertCount }}
              </span>
            </div>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ conversationAlertsSummary }}
            </p>
            <p class="mt-1 text-[11px] text-n-slate-10">
              {{ conversationAlertsSourceLabel }}
              <span v-if="automationRequestState.alertsUpdatedAt">
                · {{ automationRequestState.alertsUpdatedAt }}
              </span>
            </p>
          </div>
          <button
            type="button"
            :class="actionButtonClasses({ active: automationRequestState.isRefreshingAlerts })"
            :disabled="!!sendingKey || automationRequestState.isRefreshingAlerts"
            @click="refreshConversationAlerts"
          >
            <span class="i-lucide-radar text-sm" />
            <span>{{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.REFRESH_ALERTS') }}</span>
          </button>
        </div>

        <div
          v-if="automationRequestState.alertsError"
          class="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-600/40 dark:bg-amber-500/10 dark:text-amber-100"
        >
          {{ automationRequestState.alertsError }}
        </div>

        <div
          v-if="conversationAlerts.length"
          class="mt-3 flex flex-col gap-2"
        >
          <div
            v-for="alert in conversationAlerts"
            :key="alert.key"
            class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 dark:border-n-weak/80 dark:bg-n-slate-2"
          >
            <div class="flex flex-wrap items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="text-xs font-medium text-n-slate-12">
                  {{ alert.title }}
                </p>
                <p
                  v-if="alert.message"
                  class="mt-1 text-xs leading-5 text-n-slate-11"
                >
                  {{ alert.message }}
                </p>
              </div>
              <span :class="badgeClasses(alert.tone)">
                {{ alert.category }}
              </span>
            </div>
            <ul
              v-if="alert.details.length"
              class="mt-2 list-disc pl-4 text-xs leading-5 text-n-slate-11"
            >
              <li
                v-for="detail in alert.details"
                :key="detail"
              >
                {{ detail }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div
        v-if="!isDvAccount"
        class="mt-3 rounded-lg border border-n-weak bg-white p-3 dark:border-n-weak/80 dark:bg-n-slate-1"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.TITLE') }}
              </p>
              <span :class="badgeClasses(currentVietvedLifecycleStage ? 'brand' : 'neutral')">
                {{
                  currentVietvedLifecycleStage?.label ||
                    $t('CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.NO_STAGE')
                }}
              </span>
            </div>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.DESCRIPTION') }}
            </p>
          </div>
        </div>

        <div class="mt-3 flex flex-col gap-2">
          <div
            v-for="stage in vietvedLifecycleStages"
            :key="stage.key"
            class="rounded-lg border border-n-weak bg-n-alpha-2 p-3 dark:border-n-weak/80 dark:bg-n-slate-2"
          >
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="text-sm font-medium text-n-slate-12">
                  {{ stage.label }}
                </p>
                <p class="mt-1 text-xs leading-5 text-n-slate-11">
                  {{ stage.description }}
                </p>
              </div>
              <span
                v-if="stage.key === vietvedLifecycleStageKey"
                :class="badgeClasses('success')"
              >
                {{ $t('CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.CURRENT') }}
              </span>
            </div>
            <p class="mt-2 text-[11px] font-medium uppercase tracking-wide text-n-slate-10">
              {{ stage.actionTitle }}
            </p>
            <p class="mt-2 break-all text-xs text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.FILTER_LABEL') }}:
              <span class="font-mono text-n-slate-12">#{{ stage.conversationLabel }}</span>
            </p>
            <div class="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                :class="actionButtonClasses({ active: stage.key === vietvedLifecycleStageKey })"
                :disabled="!!sendingKey"
                @click="sendVietvedLifecycle(stage)"
              >
                <span class="i-lucide-tags text-sm" />
                <span>{{ $t('CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.SET_STAGE') }}</span>
              </button>
              <button
                type="button"
                :class="actionButtonClasses()"
                :disabled="!!sendingKey"
                @click="insertVietvedLifecycleDraft(stage)"
              >
                <span class="i-lucide-message-square-plus text-sm" />
                <span>{{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.INSERT_DRAFT') }}</span>
              </button>
              <router-link
                :to="vietvedLifecycleFilterRoute(stage)"
                :class="actionButtonClasses()"
              >
                <span class="i-lucide-filter text-sm" />
                <span>{{ $t('CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.OPEN_FILTER') }}</span>
              </router-link>
            </div>
          </div>
        </div>

        <div class="mt-3">
          <label class="mb-1 block text-xs font-medium text-n-slate-11">
            {{ $t('CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.NOTE_LABEL') }}
          </label>
          <textarea
            v-model="vietvedLifecycleNote"
            :class="textareaClasses()"
            :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.VIETVED_LIFECYCLE.NOTE_PLACEHOLDER')"
          />
        </div>
      </div>

      <div class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <button
          type="button"
          :class="actionButtonClasses()"
          :disabled="!!sendingKey"
          @click="sendRefreshContext"
        >
          <span class="i-lucide-refresh-cw text-sm" />
          <span>{{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.REFRESH_CONTEXT') }}</span>
        </button>
        <button
          type="button"
          :class="actionButtonClasses({ active: activeGlobalAction === 'booking_agent' })"
          :disabled="!!sendingKey"
          @click="openGlobalForm('booking_agent')"
        >
          <span class="i-lucide-calendar-plus text-sm" />
          <span>{{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.ADD_BOOKING') }}</span>
        </button>
        <button
          type="button"
          :class="actionButtonClasses({ active: activeGlobalAction === 'many_booking_agent' })"
          :disabled="!!sendingKey"
          @click="openGlobalForm('many_booking_agent')"
        >
          <span class="i-lucide-layers-3 text-sm" />
          <span>{{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.ADD_MANY_BOOKINGS') }}</span>
        </button>
        <button
          type="button"
          :class="actionButtonClasses({ active: activeGlobalAction === 'tour_suggestions' })"
          :disabled="!!sendingKey"
          @click="openTourSuggestions"
        >
          <span class="i-lucide-map text-sm" />
          <span>{{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.TOUR_SUGGESTIONS') }}</span>
        </button>
      </div>

      <div
        v-if="activeGlobalAction === 'booking_agent'"
        class="mt-3 rounded-xl border border-n-weak bg-white p-3 dark:border-n-weak/80 dark:bg-n-slate-1"
      >
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.SERVICE') }}
            </label>
            <input
              v-model="singleBookingForm.service"
              :class="inputClasses()"
              :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.SERVICE')"
              type="text"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.DATE') }}
            </label>
            <input
              v-model="singleBookingForm.date"
              :class="inputClasses()"
              type="date"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING_TYPE') }}
            </label>
            <select
              v-model="singleBookingForm.bookingType"
              :class="inputClasses()"
            >
              <option value="ticket">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_TYPE_OPTIONS.ticket') }}
              </option>
              <option value="group">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_TYPE_OPTIONS.group') }}
              </option>
              <option value="private">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_TYPE_OPTIONS.private') }}
              </option>
            </select>
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.HOTEL') }}
            </label>
            <input
              v-model="singleBookingForm.hotelName"
              :class="inputClasses()"
              :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.HOTEL')"
              type="text"
            />
          </div>
          <div class="grid grid-cols-1 gap-2 sm:col-span-2 sm:grid-cols-3">
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.ADULTS') }}
              </label>
              <input
                v-model.number="singleBookingForm.adults"
                :class="inputClasses()"
                min="0"
                type="number"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.CHILDREN') }}
              </label>
              <input
                v-model.number="singleBookingForm.children"
                :class="inputClasses()"
                min="0"
                type="number"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.INFANTS') }}
              </label>
              <input
                v-model.number="singleBookingForm.infants"
                :class="inputClasses()"
                min="0"
                type="number"
              />
            </div>
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.PICKUP_TIME') }}
            </label>
            <input
              v-model="singleBookingForm.pickupTime"
              :class="inputClasses()"
              placeholder="07:00"
              type="text"
            />
          </div>
          <div />
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.CONTACT_NAME') }}
            </label>
            <input
              v-model="singleBookingForm.contactName"
              :class="inputClasses()"
              :placeholder="defaultContactName || $t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.CONTACT_NAME')"
              type="text"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.PHONE') }}
            </label>
            <input
              v-model="singleBookingForm.contactPhone"
              :class="inputClasses()"
              :placeholder="defaultContactPhone || $t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.PHONE')"
              type="text"
            />
          </div>
          <div class="sm:col-span-2">
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.EMAIL') }}
            </label>
            <input
              v-model="singleBookingForm.contactEmail"
              :class="inputClasses()"
              :placeholder="defaultContactEmail || $t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.EMAIL')"
              type="email"
            />
          </div>
          <div class="sm:col-span-2">
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.NOTE') }}
            </label>
            <textarea
              v-model="singleBookingForm.note"
              :class="textareaClasses()"
              :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.NOTE')"
            />
          </div>
        </div>

        <div class="mt-3 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            :class="actionButtonClasses()"
            :disabled="!!sendingKey"
            @click="activeGlobalAction = ''"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.CLOSE_FORM') }}
          </button>
          <button
            type="button"
            :class="actionButtonClasses({ active: true })"
            :disabled="sendingKey === 'booking_agent'"
            @click="sendSingleBooking"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.SEND_COMMAND') }}
          </button>
        </div>
      </div>

      <div
        v-if="activeGlobalAction === 'many_booking_agent'"
        class="mt-3 rounded-xl border border-n-weak bg-white p-3 dark:border-n-weak/80 dark:bg-n-slate-1"
      >
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.CONTACT_NAME') }}
            </label>
            <input
              v-model="manyBookingCommonForm.contactName"
              :class="inputClasses()"
              :placeholder="defaultContactName || $t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.CONTACT_NAME')"
              type="text"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.PHONE') }}
            </label>
            <input
              v-model="manyBookingCommonForm.contactPhone"
              :class="inputClasses()"
              :placeholder="defaultContactPhone || $t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.PHONE')"
              type="text"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.EMAIL') }}
            </label>
            <input
              v-model="manyBookingCommonForm.contactEmail"
              :class="inputClasses()"
              :placeholder="defaultContactEmail || $t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.EMAIL')"
              type="email"
            />
          </div>
          <div class="sm:col-span-3">
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.MANAGER_NOTE') }}
            </label>
            <textarea
              v-model="manyBookingCommonForm.managerNote"
              :class="textareaClasses()"
              :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.MANAGER_NOTE')"
            />
          </div>
        </div>

        <div class="mt-3 flex flex-col gap-3">
          <div
            v-for="(row, index) in manyBookingRows"
            :key="row.id"
            class="rounded-xl border border-n-weak bg-n-alpha-2 p-3 dark:border-n-weak/80 dark:bg-n-slate-2"
          >
            <div class="mb-2 flex items-center justify-between gap-2">
              <p class="text-xs font-medium text-n-slate-12">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.MANY_BOOKING_ROW_TITLE', { index: index + 1 }) }}
              </p>
              <button
                type="button"
                :class="actionButtonClasses({ danger: true })"
                :disabled="!!sendingKey"
                @click="removeManyBookingRow(row.id)"
              >
                {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.REMOVE_ROW') }}
              </button>
            </div>

            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.SERVICE') }}
                </label>
                <input
                  v-model="row.service"
                  :class="inputClasses()"
                  :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.SERVICE')"
                  type="text"
                />
              </div>
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.DATE') }}
                </label>
                <input
                  v-model="row.date"
                  :class="inputClasses()"
                  type="date"
                />
              </div>
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.BOOKING_TYPE') }}
                </label>
                <select
                  v-model="row.bookingType"
                  :class="inputClasses()"
                >
                  <option value="ticket">
                    {{ $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_TYPE_OPTIONS.ticket') }}
                  </option>
                  <option value="group">
                    {{ $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_TYPE_OPTIONS.group') }}
                  </option>
                  <option value="private">
                    {{ $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_TYPE_OPTIONS.private') }}
                  </option>
                </select>
              </div>
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.HOTEL') }}
                </label>
                <input
                  v-model="row.hotelName"
                  :class="inputClasses()"
                  :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.HOTEL')"
                  type="text"
                />
              </div>
              <div class="grid grid-cols-1 gap-2 sm:col-span-2 sm:grid-cols-3">
                <div>
                  <label class="mb-1 block text-xs font-medium text-n-slate-11">
                    {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.ADULTS') }}
                  </label>
                  <input
                    v-model.number="row.adults"
                    :class="inputClasses()"
                    min="0"
                    type="number"
                  />
                </div>
                <div>
                  <label class="mb-1 block text-xs font-medium text-n-slate-11">
                    {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.CHILDREN') }}
                  </label>
                  <input
                    v-model.number="row.children"
                    :class="inputClasses()"
                    min="0"
                    type="number"
                  />
                </div>
                <div>
                  <label class="mb-1 block text-xs font-medium text-n-slate-11">
                    {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.INFANTS') }}
                  </label>
                  <input
                    v-model.number="row.infants"
                    :class="inputClasses()"
                    min="0"
                    type="number"
                  />
                </div>
              </div>
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.PICKUP_TIME') }}
                </label>
                <input
                  v-model="row.pickupTime"
                  :class="inputClasses()"
                  placeholder="07:00"
                  type="text"
                />
              </div>
              <div class="sm:col-span-2">
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.NOTE') }}
                </label>
                <textarea
                  v-model="row.note"
                  :class="textareaClasses()"
                  :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.NOTE')"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="mt-3 flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            :class="actionButtonClasses()"
            :disabled="!!sendingKey"
            @click="addManyBookingRow"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.ADD_ROW') }}
          </button>
          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              :class="actionButtonClasses()"
              :disabled="!!sendingKey"
              @click="activeGlobalAction = ''"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.CLOSE_FORM') }}
            </button>
            <button
              type="button"
              :class="actionButtonClasses({ active: true })"
              :disabled="sendingKey === 'many_booking_agent'"
              @click="sendManyBookings"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.SEND_COMMAND') }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="activeGlobalAction === 'tour_suggestions'"
        class="mt-3 rounded-xl border border-n-weak bg-white p-3 dark:border-n-weak/80 dark:bg-n-slate-1"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.TOUR_SUGGESTIONS.TITLE') }}
            </p>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.TOUR_SUGGESTIONS.DESCRIPTION') }}
            </p>
            <p class="mt-1 text-[11px] text-n-slate-10">
              {{ tourSuggestionsSourceLabel }}
              <span v-if="automationRequestState.suggestionsUpdatedAt">
                · {{ automationRequestState.suggestionsUpdatedAt }}
              </span>
            </p>
          </div>
          <span :class="badgeClasses(displayedTourSuggestions.length ? 'brand' : 'neutral')">
            {{ displayedTourSuggestions.length }}
          </span>
        </div>

        <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.DATE_FROM') }}
            </label>
            <input
              v-model="tourSuggestionForm.dateFrom"
              :class="inputClasses()"
              type="date"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.DATE_TO') }}
            </label>
            <input
              v-model="tourSuggestionForm.dateTo"
              :class="inputClasses()"
              type="date"
            />
          </div>
          <div class="grid grid-cols-1 gap-2 sm:col-span-2 sm:grid-cols-3">
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.ADULTS') }}
              </label>
              <input
                v-model.number="tourSuggestionForm.adults"
                :class="inputClasses()"
                min="0"
                type="number"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.CHILDREN') }}
              </label>
              <input
                v-model.number="tourSuggestionForm.children"
                :class="inputClasses()"
                min="0"
                type="number"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.INFANTS') }}
              </label>
              <input
                v-model.number="tourSuggestionForm.infants"
                :class="inputClasses()"
                min="0"
                type="number"
              />
            </div>
          </div>
          <div class="sm:col-span-2">
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.HOTEL') }}
            </label>
            <input
              v-model="tourSuggestionForm.hotelName"
              :class="inputClasses()"
              :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.HOTEL')"
              type="text"
            />
          </div>
          <div class="sm:col-span-2">
            <label class="mb-1 block text-xs font-medium text-n-slate-11">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.INTEREST') }}
            </label>
            <textarea
              v-model="tourSuggestionForm.interest"
              :class="textareaClasses()"
              :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.TOUR_INTEREST')"
            />
          </div>
        </div>

        <div
          v-if="automationRequestState.suggestionsError"
          class="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-600/40 dark:bg-amber-500/10 dark:text-amber-100"
        >
          {{ automationRequestState.suggestionsError }}
        </div>

        <div
          v-if="displayedTourSuggestions.length"
          class="mt-3 flex flex-col gap-2"
        >
          <div
            v-for="suggestion in displayedTourSuggestions"
            :key="suggestion.key"
            class="rounded-lg border border-n-weak bg-n-alpha-2 p-3 dark:border-n-weak/80 dark:bg-n-slate-2"
          >
            <div class="flex flex-wrap items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="text-sm font-medium text-n-slate-12">
                  {{ suggestion.serviceTitle }}
                </p>
                <div class="mt-2 flex flex-wrap items-center gap-2">
                  <span :class="badgeClasses('brand')">
                    {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.AVAILABILITY') }}:
                    {{ formatSuggestionAvailability(suggestion) }}
                  </span>
                  <span :class="badgeClasses('neutral')">
                    {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.PRICE') }}:
                    {{ formatSuggestionPrice(suggestion) }}
                  </span>
                </div>
              </div>
              <button
                v-if="suggestion.draftText"
                type="button"
                :class="actionButtonClasses()"
                @click="insertSuggestionDraft(suggestion)"
              >
                {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.INSERT_DRAFT') }}
              </button>
            </div>

            <div
              v-if="suggestion.reasons.length || suggestion.warnings.length"
              class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2"
            >
              <div v-if="suggestion.reasons.length">
                <p class="text-[11px] font-medium uppercase tracking-wide text-n-slate-10">
                  {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.REASONS') }}
                </p>
                <ul class="mt-1 list-disc pl-4 text-xs leading-5 text-n-slate-11">
                  <li
                    v-for="reason in suggestion.reasons"
                    :key="reason"
                  >
                    {{ reason }}
                  </li>
                </ul>
              </div>
              <div v-if="suggestion.warnings.length">
                <p class="text-[11px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-100">
                  {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.WARNINGS') }}
                </p>
                <ul class="mt-1 list-disc pl-4 text-xs leading-5 text-n-slate-11">
                  <li
                    v-for="warning in suggestion.warnings"
                    :key="warning"
                  >
                    {{ warning }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else
          class="mt-3 rounded-lg border border-dashed border-n-weak bg-n-alpha-2 px-3 py-3 text-xs leading-5 text-n-slate-11 dark:border-n-weak/80 dark:bg-n-slate-2"
        >
          {{ $t('CONTACT_PANEL.ACTION_CENTER.TOUR_SUGGESTIONS.EMPTY') }}
        </div>

        <div class="mt-3 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            :class="actionButtonClasses()"
            :disabled="!!sendingKey || automationRequestState.isLoadingSuggestions"
            @click="activeGlobalAction = ''"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.CLOSE_FORM') }}
          </button>
          <button
            type="button"
            :class="actionButtonClasses({ active: true })"
            :disabled="!!sendingKey || automationRequestState.isLoadingSuggestions"
            @click="requestTourSuggestions"
          >
            {{
              automationRequestState.isLoadingSuggestions
                ? $t('CONTACT_PANEL.ACTION_CENTER.TOUR_SUGGESTIONS.LOADING')
                : $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.LOAD_SUGGESTIONS')
            }}
          </button>
        </div>
      </div>
      </template>
    </div>

    <section v-if="isDvAccount" class="mt-3">
      <div class="mb-2 flex items-center justify-between px-1">
        <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
          {{ $t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.TITLE') }}
        </span>
        <span v-if="hasBookings" :class="dvChipClasses('neutral')">
          {{ dvBookingCards.length }}
        </span>
      </div>

      <div v-if="hasBookings" class="flex flex-col gap-2">
        <article
          v-for="booking in dvBookingCards"
          :key="booking.key"
          class="rounded-xl border border-n-weak bg-white px-3 py-2.5 dark:border-n-weak/80 dark:bg-n-slate-1"
          :class="{ 'opacity-60': booking.cancelled }"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="mb-0 truncate text-sm font-semibold leading-5 text-n-slate-12">
                {{ booking.tour || $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_CARD.UNKNOWN_TOUR') }}
              </p>
              <p class="mb-0 mt-0.5 text-xs leading-4 text-n-slate-11">
                <span v-if="booking.bookingId">#{{ booking.bookingId }} · </span>{{ booking.dateLabel }}
              </p>
            </div>
            <span :class="dvChipClasses(booking.statusTone)">
              {{ booking.statusLabel }}
            </span>
          </div>

          <dl class="mb-0 mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs leading-4">
            <dt class="text-n-slate-10">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.PICKUP') }}
            </dt>
            <dd
              class="mb-0 text-right font-medium"
              :class="booking.pickupPublishedTime ? 'text-n-slate-12' : DV_TONE_TEXT.warning"
            >
              {{ booking.pickupLabel }}
            </dd>
            <template v-if="booking.guestsLabel">
              <dt class="text-n-slate-10">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.GUESTS') }}
              </dt>
              <dd class="mb-0 text-right text-n-slate-12">{{ booking.guestsLabel }}</dd>
            </template>
            <template v-if="booking.guideName">
              <dt class="text-n-slate-10">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.GUIDE') }}
              </dt>
              <dd class="mb-0 truncate text-right text-n-slate-12">{{ booking.guideName }}</dd>
            </template>
            <template v-if="booking.hotelName">
              <dt class="text-n-slate-10">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.HOTEL') }}
              </dt>
              <dd class="mb-0 truncate text-right text-n-slate-12" :title="booking.hotelName">
                {{ booking.hotelName }}
              </dd>
            </template>
            <dt class="text-n-slate-10">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.PAYMENT') }}
            </dt>
            <dd
              class="mb-0 text-right font-medium"
              :class="DV_TONE_TEXT[booking.paymentTone]"
            >
              {{ booking.paymentLabel }}
            </dd>
          </dl>

          <p
            v-for="alert in booking.alerts"
            :key="alert.key"
            class="mb-0 mt-2 flex items-start gap-1.5 rounded-lg bg-n-ruby-3 px-2 py-1.5 text-[11px] leading-4 text-n-ruby-11"
          >
            <span class="i-lucide-triangle-alert mt-px shrink-0 text-xs" />
            <span>{{ alert.message || alert.title }}</span>
          </p>

          <button
            v-if="hasSeveralPortalLinks && booking.clientPortalUrl"
            type="button"
            class="mt-2 inline-flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
            @click="openBookingPortal(booking)"
          >
            <span class="i-lucide-key-round text-xs" />
            {{ $t('CONTACT_PANEL.ACTION_CENTER.CLIENT_PORTAL.TITLE') }}
          </button>
        </article>
      </div>

      <p
        v-else
        class="mb-0 rounded-xl border border-dashed border-n-weak px-3 py-3 text-xs text-n-slate-10 dark:border-n-weak/80"
      >
        {{ $t('CONTACT_PANEL.ACTION_CENTER.DV_BOOKINGS.EMPTY') }}
      </p>
    </section>

    <template v-if="!isDvAccount">
    <div
      v-if="hasBookings"
      class="mt-3 flex flex-col gap-3"
    >
      <div
        v-for="booking in normalizedBookings"
        :key="booking.key"
        class="rounded-xl border border-n-weak bg-white p-3 dark:border-n-weak/80 dark:bg-n-slate-1"
      >
        <div class="flex flex-wrap items-start justify-between gap-2">
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <p class="text-sm font-semibold text-n-slate-12">
                {{ booking.bookingId ? `#${booking.bookingId}` : $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_CARD.NO_ID') }}
              </p>
              <span :class="badgeClasses(resolvePickupStatus(booking).tone)">
                {{ resolvePickupStatus(booking).label }}
              </span>
              <span :class="badgeClasses(resolveDocsStatus(booking).tone)">
                {{ resolveDocsStatus(booking).label }}
              </span>
            </div>
            <p class="mt-1 text-sm text-n-slate-12 dark:text-n-slate-12">
              {{ booking.tour || $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_CARD.UNKNOWN_TOUR') }}
            </p>
            <p
              v-if="booking.summaryLine"
              class="mt-1 text-xs text-n-slate-11 dark:text-n-slate-11"
            >
              {{ booking.summaryLine }}
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <span :class="badgeClasses('neutral')">
              {{ bookingStatusLabel(booking.status) }}
            </span>
            <span :class="badgeClasses(resolveQrStatus(booking).tone)">
              {{ resolveQrStatus(booking).label }}
            </span>
          </div>
        </div>

        <div class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 dark:border-n-weak/80 dark:bg-n-slate-2">
            <p class="text-[11px] font-medium uppercase tracking-wide text-n-slate-10">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.DATE') }}
            </p>
            <p class="mt-1 text-sm text-n-slate-12">
              {{ formatDate(booking.startDate) }}
            </p>
          </div>
          <div class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 dark:border-n-weak/80 dark:bg-n-slate-2">
            <p class="text-[11px] font-medium uppercase tracking-wide text-n-slate-10">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.PAYMENT') }}
            </p>
            <p class="mt-1 text-sm text-n-slate-12">
              {{ paymentStatusLabel(booking.paymentStatus) }}
            </p>
            <p
              v-if="booking.amountDue !== null"
              class="mt-1 text-xs text-n-slate-11"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.AMOUNT_DUE') }}:
              {{ formatMoney(booking.amountDue) }}
            </p>
          </div>
          <div class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 dark:border-n-weak/80 dark:bg-n-slate-2">
            <p class="text-[11px] font-medium uppercase tracking-wide text-n-slate-10">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.GUIDE') }}
            </p>
            <p class="mt-1 text-sm text-n-slate-12">
              {{ booking.guideName || $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_CARD.GUIDE_UNASSIGNED') }}
            </p>
          </div>
          <div class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 dark:border-n-weak/80 dark:bg-n-slate-2">
            <p class="text-[11px] font-medium uppercase tracking-wide text-n-slate-10">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.HOTEL') }}
            </p>
            <p class="mt-1 text-sm text-n-slate-12">
              {{ booking.hotelName || $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_CARD.HOTEL_UNSET') }}
            </p>
          </div>
        </div>

        <div
          v-if="booking.guests || booking.clientPortalUrl || booking.guidePickupDetails"
          class="mt-3 flex flex-wrap items-center gap-2 text-xs text-n-slate-11"
        >
          <span
            v-if="booking.guests"
            :class="badgeClasses('neutral')"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.GUESTS') }}:
            {{ formatGuests(booking.guests) }}
          </span>
          <a
            v-if="booking.clientPortalUrl"
            :href="booking.clientPortalUrl"
            target="_blank"
            rel="noopener noreferrer"
            :class="actionButtonClasses()"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.OPEN_PORTAL') }}
          </a>
          <span
            v-if="booking.guidePickupDetails"
            :class="badgeClasses('brand')"
          >
            {{ booking.guidePickupDetails }}
          </span>
        </div>

        <div class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <button
            type="button"
            :class="actionButtonClasses({ active: isBookingFormOpen(booking, 'update_booking') })"
            :disabled="!!sendingKey"
            @click="openBookingForm(booking, 'update_booking')"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.UPDATE_BOOKING') }}
          </button>
          <button
            type="button"
            :class="actionButtonClasses({ active: isBookingFormOpen(booking, 'assign_guide') })"
            :disabled="!!sendingKey"
            @click="openBookingForm(booking, 'assign_guide')"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.ASSIGN_GUIDE') }}
          </button>
          <button
            type="button"
            :class="actionButtonClasses({ active: isBookingFormOpen(booking, 'set_booking_status') })"
            :disabled="!!sendingKey"
            @click="openBookingForm(booking, 'set_booking_status')"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.SET_STATUS') }}
          </button>
          <button
            type="button"
            :class="actionButtonClasses({ active: isBookingFormOpen(booking, 'generate_payment_qr') })"
            :disabled="!!sendingKey"
            @click="openBookingForm(booking, 'generate_payment_qr')"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.GENERATE_PAYMENT_QR') }}
          </button>
          <button
            type="button"
            :class="actionButtonClasses({ active: isBookingFormOpen(booking, 'qr_status') })"
            :disabled="!!sendingKey"
            @click="openBookingForm(booking, 'qr_status')"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.QR_STATUS') }}
          </button>
          <button
            type="button"
            :class="actionButtonClasses()"
            :disabled="!!sendingKey"
            @click="sendPickupStatus(booking)"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.PICKUP_STATUS') }}
          </button>
          <button
            type="button"
            :class="actionButtonClasses()"
            :disabled="!!sendingKey"
            @click="sendDocsStatus(booking)"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.DOCS_STATUS') }}
          </button>
          <button
            v-if="normalizeBookingStatusValue(booking.status) !== 'cancelled'"
            type="button"
            :class="actionButtonClasses({ active: isBookingFormOpen(booking, 'cancel_booking'), danger: true })"
            :disabled="!!sendingKey"
            @click="openBookingForm(booking, 'cancel_booking')"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.CANCEL_BOOKING') }}
          </button>
          <button
            v-else
            type="button"
            :class="actionButtonClasses()"
            :disabled="!!sendingKey"
            @click="sendRestoreBooking(booking)"
          >
            {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.RESTORE_BOOKING') }}
          </button>
        </div>

        <div
          v-if="isBookingFormOpen(booking, 'update_booking')"
          class="mt-3 rounded-xl border border-n-weak bg-n-alpha-2 p-3 dark:border-n-weak/80 dark:bg-n-slate-2"
        >
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.NEW_DATE') }}
              </label>
              <input
                v-model="ensureUpdateForm(booking).newDate"
                :class="inputClasses()"
                type="date"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.HOTEL') }}
              </label>
              <input
                v-model="ensureUpdateForm(booking).hotelName"
                :class="inputClasses()"
                :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.HOTEL')"
                type="text"
              />
            </div>
            <div class="grid grid-cols-1 gap-2 sm:col-span-2 sm:grid-cols-3">
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.ADULTS') }}
                </label>
                <input
                  v-model.number="ensureUpdateForm(booking).adults"
                  :class="inputClasses()"
                  min="0"
                  type="number"
                />
              </div>
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.CHILDREN') }}
                </label>
                <input
                  v-model.number="ensureUpdateForm(booking).children"
                  :class="inputClasses()"
                  min="0"
                  type="number"
                />
              </div>
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.INFANTS') }}
                </label>
                <input
                  v-model.number="ensureUpdateForm(booking).infants"
                  :class="inputClasses()"
                  min="0"
                  type="number"
                />
              </div>
            </div>
          </div>
          <div class="mt-3 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              :class="actionButtonClasses()"
              :disabled="!!sendingKey"
              @click="activeBookingAction = ''"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.CLOSE_FORM') }}
            </button>
            <button
              type="button"
              :class="actionButtonClasses({ active: true })"
              :disabled="sendingKey === bookingActionKey(booking, 'update_booking')"
              @click="sendUpdateBooking(booking)"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.SEND_COMMAND') }}
            </button>
          </div>
        </div>

        <div
          v-if="isBookingFormOpen(booking, 'assign_guide')"
          class="mt-3 rounded-xl border border-n-weak bg-n-alpha-2 p-3 dark:border-n-weak/80 dark:bg-n-slate-2"
        >
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.GUIDE') }}
              </label>
              <input
                v-model="ensureAssignGuideForm(booking).guide"
                :class="inputClasses()"
                :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.GUIDE')"
                type="text"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.GUIDE_ID') }}
              </label>
              <input
                v-model="ensureAssignGuideForm(booking).guideId"
                :class="inputClasses()"
                :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.GUIDE_ID')"
                type="text"
              />
            </div>
            <div class="sm:col-span-2">
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.NOTE') }}
              </label>
              <textarea
                v-model="ensureAssignGuideForm(booking).note"
                :class="textareaClasses()"
                :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.NOTE')"
              />
            </div>
          </div>
          <div class="mt-3 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              :class="actionButtonClasses()"
              :disabled="!!sendingKey"
              @click="activeBookingAction = ''"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.CLOSE_FORM') }}
            </button>
            <button
              type="button"
              :class="actionButtonClasses({ active: true })"
              :disabled="sendingKey === bookingActionKey(booking, 'assign_guide')"
              @click="sendAssignGuide(booking)"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.SEND_COMMAND') }}
            </button>
          </div>
        </div>

        <div
          v-if="isBookingFormOpen(booking, 'set_booking_status')"
          class="mt-3 rounded-xl border border-n-weak bg-n-alpha-2 p-3 dark:border-n-weak/80 dark:bg-n-slate-2"
        >
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.STATUS') }}
              </label>
              <select
                v-model="ensureSetStatusForm(booking).status"
                :class="inputClasses()"
              >
                <option value="pending">{{ $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_STATUS.pending') }}</option>
                <option value="confirmed">{{ $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_STATUS.confirmed') }}</option>
                <option value="time_sent">{{ $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_STATUS.time_sent') }}</option>
                <option value="deposit">{{ $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_STATUS.deposit') }}</option>
                <option value="paid">{{ $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_STATUS.paid') }}</option>
                <option value="completed">{{ $t('CONTACT_PANEL.ACTION_CENTER.BOOKING_STATUS.completed') }}</option>
              </select>
            </div>
            <div />
            <div class="sm:col-span-2">
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.NOTE') }}
              </label>
              <textarea
                v-model="ensureSetStatusForm(booking).note"
                :class="textareaClasses()"
                :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.NOTE')"
              />
            </div>
          </div>
          <div class="mt-3 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              :class="actionButtonClasses()"
              :disabled="!!sendingKey"
              @click="activeBookingAction = ''"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.CLOSE_FORM') }}
            </button>
            <button
              type="button"
              :class="actionButtonClasses({ active: true })"
              :disabled="sendingKey === bookingActionKey(booking, 'set_booking_status')"
              @click="sendSetBookingStatus(booking)"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.SEND_COMMAND') }}
            </button>
          </div>
        </div>

        <div
          v-if="isBookingFormOpen(booking, 'generate_payment_qr')"
          class="mt-3 rounded-xl border border-n-weak bg-n-alpha-2 p-3 dark:border-n-weak/80 dark:bg-n-slate-2"
        >
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.PAYMENT_TYPE') }}
              </label>
              <select
                v-model="ensureGenerateQrForm(booking).paymentType"
                :class="inputClasses()"
              >
                <option value="deposit">
                  {{ $t('CONTACT_PANEL.ACTION_CENTER.PAYMENT_TYPE_OPTIONS.deposit') }}
                </option>
                <option value="full">
                  {{ $t('CONTACT_PANEL.ACTION_CENTER.PAYMENT_TYPE_OPTIONS.full') }}
                </option>
              </select>
            </div>
            <div
              v-if="ensureGenerateQrForm(booking).paymentType === 'deposit'"
            >
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.DEPOSIT_AMOUNT') }}
              </label>
              <input
                v-model="ensureGenerateQrForm(booking).depositAmount"
                :class="inputClasses()"
                :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.DEPOSIT_AMOUNT')"
                type="text"
              />
            </div>
            <div class="sm:col-span-2">
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.NOTE') }}
              </label>
              <textarea
                v-model="ensureGenerateQrForm(booking).note"
                :class="textareaClasses()"
                :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.NOTE')"
              />
            </div>
          </div>
          <div class="mt-3 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              :class="actionButtonClasses()"
              :disabled="!!sendingKey"
              @click="activeBookingAction = ''"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.CLOSE_FORM') }}
            </button>
            <button
              type="button"
              :class="actionButtonClasses({ active: true })"
              :disabled="sendingKey === bookingActionKey(booking, 'generate_payment_qr')"
              @click="sendGeneratePaymentQr(booking)"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.SEND_COMMAND') }}
            </button>
          </div>
        </div>

        <div
          v-if="isBookingFormOpen(booking, 'qr_status')"
          class="mt-3 rounded-xl border border-n-weak bg-n-alpha-2 p-3 dark:border-n-weak/80 dark:bg-n-slate-2"
        >
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input
              v-model="ensureQrStatusForm(booking).refreshProviderStatus"
              class="rounded border-n-weak text-n-brand focus:ring-n-brand"
              type="checkbox"
            />
            <span>
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.PROVIDER_REFRESH') }}
            </span>
          </label>
          <div class="mt-3 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              :class="actionButtonClasses()"
              :disabled="!!sendingKey"
              @click="activeBookingAction = ''"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.CLOSE_FORM') }}
            </button>
            <button
              type="button"
              :class="actionButtonClasses({ active: true })"
              :disabled="sendingKey === bookingActionKey(booking, 'qr_status_send')"
              @click="sendQrStatus(booking)"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.SEND_COMMAND') }}
            </button>
          </div>
        </div>

        <div
          v-if="isBookingFormOpen(booking, 'cancel_booking')"
          class="mt-3 rounded-xl border border-rose-200 bg-rose-50/70 p-3 dark:border-rose-600/40 dark:bg-rose-500/10"
        >
          <div>
            <label class="mb-1 block text-xs font-medium text-rose-700 dark:text-rose-200">
              {{ $t('CONTACT_PANEL.ACTION_CENTER.FIELDS.REASON') }}
            </label>
            <textarea
              v-model="ensureCancelForm(booking).reason"
              :class="textareaClasses()"
              :placeholder="$t('CONTACT_PANEL.ACTION_CENTER.PLACEHOLDERS.CANCEL_REASON')"
            />
          </div>
          <div class="mt-3 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              :class="actionButtonClasses()"
              :disabled="!!sendingKey"
              @click="activeBookingAction = ''"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.CLOSE_FORM') }}
            </button>
            <button
              type="button"
              :class="actionButtonClasses({ danger: true })"
              :disabled="sendingKey === bookingActionKey(booking, 'cancel_booking_send')"
              @click="sendCancelBooking(booking)"
            >
              {{ $t('CONTACT_PANEL.ACTION_CENTER.ACTIONS.SEND_COMMAND') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="mt-3 rounded-xl border border-dashed border-n-weak bg-n-alpha-2 px-3 py-4 text-sm text-n-slate-11 dark:border-n-weak/80 dark:bg-n-slate-2 dark:text-n-slate-11"
    >
      {{ $t('CONTACT_PANEL.ACTION_CENTER.EMPTY_BOOKINGS') }}
    </div>
    </template>
  </div>
</template>
